import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FaEye, FaGlobeAmericas, FaSearchMinus, FaSearchPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { verificarColunas, mesclarColuna, extrasDaMescla, calcularTotal, tktPorPessoaStr, tktPorGrupoStr, tktPorGrupoNum, vinculoDe, IDA_KEYS, RET_KEYS, textoDasLinhas, camposDoColar, ladosDaCopia, tarifaComTaxas, cidadesDoAeroporto, createEmptyOpcao } from '../util/malhaAereo';
import { moeda } from '../util/util';
import MenuContextoMalha, { lerCopiaDaMalha, guardarCopiaDaMalha, lerParaColar, AVISO_CLIPBOARD_INVALIDO } from './MenuContextoMalha';
import ConfirmarAcaoModal from './ConfirmarAcaoModal';
import '../css/financeiro-parcela-pagar.css';

/**
 * A planilha da malha aérea — a mesma tabela na tela da malha e na Importação das Opções de
 * Aéreo. Agrupa as opções por vínculo (trecho ou grupo, com mesclagem por valor), alterna entre
 * o padrão de exportação do cliente e as informações completas, redimensiona colunas e aproxima
 * ou afasta (zoom). Os campos só se editam com `editavel` (o Aéreo, na tela da malha).
 *
 * Com `selecao`, ganha a coluna "Importar": a ida e o retorno de cada opção se marcam separados.
 * O estado de cada lado (se pode marcar, e por quê) vem de quem usa a tabela.
 *
 * Com `onExcluir`, a linha se marca pelo clique na rota (a 1ª coluna): ela veste o mesmo véu do
 * lado marcado para importar, a rota fica em negrito, o botão Excluir aparece ao lado do zoom e o
 * "Todos" do cabeçalho azul marca todas. Toda linha se marca, porque a marca também serve ao clique
 * direito; a que não sai (`motivoNaoExcluir`) fica na malha no Excluir. No grupo marcado em parte, a mescla só junta
 * linhas vizinhas no mesmo estado, sem mudar a ordem. Sem checkbox: numa planilha desta largura, mais
 * uma coluna de caixinhas seria ruído.
 *
 * Com `onAlterarOpcoes` e `editavel`, o clique direito numa célula abre o `MenuContextoMalha`:
 * destacar a célula (na 1ª coluna, a linha inteira) com cor de texto ou de fundo, copiar a ida, a
 * volta ou as duas, e colar. Copiar e colar alcançam as linhas marcadas quando a clicada é uma delas,
 * e a tabela mostra o alcance: as linhas do alvo com o menu aberto, as células do item sob o mouse e
 * o que está copiado, tracejado até o Esc. Com `onDuplicar`, o Duplicar faz da cópia linhas novas.
 * O destaque mora em `destaques` de cada opção: { [campo]: { texto, fundo, negrito, italico, sublinhado } }, e `_linha` para a
 * linha inteira (a célula vale por cima dela).
 */

// Chave do destaque da linha inteira em `destaques`.
const LINHA = '_linha';

const BRAND_NAVY   = '#1f3c88';
const BRAND_BLUE   = '#2d7ff9';
const BRAND_SKY    = '#eef6ff';
const BRAND_BORDER = '#dbe8f8';

// Definição das colunas — fonte única de verdade para header, colgroup e tbody
// control: 1ª coluna (botão de toggle + rota IATA ida/volta). detail: só no modo expandido (Percurso).
// compactOnly: só no modo compacto. field: campo de dados quando a key difere (colunas Percurso).
const COLS = [
    { key: '__toggle',            label: '',             group: null,            control: true, toggle: true },
    { key: 'idaOrigemCidade',     label: 'Origem',       group: null,            compactOnly: true },
    { key: 'opcao',               label: 'Opção',        group: null },

    // PERCURSO IDA (modo expandido) — granular editável
    { key: 'p_idaOrigemIata',     label: 'IATA Orig.',   group: 'percursoIda',   detail: true, field: 'idaOrigemIata' },
    { key: 'p_idaOrigemCidade',   label: 'Cidade Orig.', group: 'percursoIda',   detail: true, field: 'idaOrigemCidade' },
    { key: 'p_idaOrigemAeroporto',label: 'Aero. Orig.',  group: 'percursoIda',   detail: true, field: 'idaOrigemAeroporto' },
    { key: 'p_idaDestinoIata',    label: 'IATA Dest.',   group: 'percursoIda',   detail: true, field: 'idaDestinoIata' },
    { key: 'p_idaDestinoCidade',  label: 'Cidade Dest.', group: 'percursoIda',   detail: true, field: 'idaDestinoCidade' },
    { key: 'p_idaDestinoAeroporto',label:'Aero. Dest.',  group: 'percursoIda',   detail: true, field: 'idaDestinoAeroporto' },

    // IDA (modo compacto: aeroporto)
    { key: 'idaData',             label: 'Data',         group: 'ida' },
    { key: 'idaCia',              label: 'Cia',          group: 'ida' },
    { key: 'idaVoo',              label: 'Voo',          group: 'ida' },
    { key: 'idaOrigemAeroporto',  label: 'Origem',       group: 'ida',           compactOnly: true },
    { key: 'idaDestinoAeroporto', label: 'Destino',      group: 'ida',           compactOnly: true },
    { key: 'idaSaida',            label: 'Saída',        group: 'ida' },
    { key: 'idaChegada',          label: 'Chegada',      group: 'ida' },

    // PERCURSO VOLTA (modo expandido)
    { key: 'p_retOrigemIata',     label: 'IATA Orig.',   group: 'percursoVolta', detail: true, field: 'retOrigemIata' },
    { key: 'p_retOrigemCidade',   label: 'Cidade Orig.', group: 'percursoVolta', detail: true, field: 'retOrigemCidade' },
    { key: 'p_retOrigemAeroporto',label: 'Aero. Orig.',  group: 'percursoVolta', detail: true, field: 'retOrigemAeroporto' },
    { key: 'p_retDestinoIata',    label: 'IATA Dest.',   group: 'percursoVolta', detail: true, field: 'retDestinoIata' },
    { key: 'p_retDestinoCidade',  label: 'Cidade Dest.', group: 'percursoVolta', detail: true, field: 'retDestinoCidade' },
    { key: 'p_retDestinoAeroporto',label:'Aero. Dest.',  group: 'percursoVolta', detail: true, field: 'retDestinoAeroporto' },

    // RETORNO (modo compacto)
    { key: 'retData',             label: 'Data',         group: 'retorno' },
    { key: 'retCia',              label: 'Cia',          group: 'retorno' },
    { key: 'retVoo',              label: 'Voo',          group: 'retorno' },
    { key: 'retOrigemAeroporto',  label: 'Origem',       group: 'retorno',       compactOnly: true },
    { key: 'retDestinoAeroporto', label: 'Destino',      group: 'retorno',       compactOnly: true },
    { key: 'retSaida',            label: 'Saída',        group: 'retorno' },
    { key: 'retChegada',          label: 'Chegada',      group: 'retorno' },

    // VALORES / PAX / OBS
    // Tarifa e taxas de cada lado numa coluna só (o pedido do Aéreo de 05/10), como no Modelo Cliente.
    // É só exibição: os dois campos seguem gravados e, na visualização completa (o olho), aparecem e
    // se editam em separado. `soma` diz o lado; `field` leva o destaque da célula para a tarifa do lado.
    { key: 'tarifaTaxasIda',      label: 'Tarifa & Taxas Ida',  group: 'valores', compactOnly: true, soma: 'ida', field: 'tarifaIda' },
    { key: 'tarifaTaxasRet',      label: 'Tarifa & Taxas Ret.', group: 'valores', compactOnly: true, soma: 'ret', field: 'tarifaRet' },
    { key: 'tarifaIda',           label: 'Tarifa Ida',   group: 'valores',           detail: true },
    { key: 'taxasIda',            label: 'Taxas Ida',    group: 'valores',           detail: true },
    { key: 'tarifaRet',           label: 'Tarifa Ret.',  group: 'valores',           detail: true },
    { key: 'taxasRet',            label: 'Taxas Ret.',   group: 'valores',           detail: true },
    { key: 'total',               label: 'Total',        group: 'valores' },
    { key: 'tktPessoa',           label: 'TKT/Pax',      group: 'valores' },
    { key: 'tktGrupo',            label: 'TKT/Grupo',    group: 'valores' },
    { key: 'numParticipantes',    label: 'Num. Part.',   group: 'pax',           numeric: true },
    { key: 'obs',                 label: 'Obs.',         group: 'obs' },
];

// A coluna da importação entra logo depois da "Opção", no mesmo grupo sem título.
// Uma coluna de seleção POR LADO, cada uma imediatamente à esquerda do bloco dela: é onde o olho
// já está quando decide levar aquele voo. Numa só, no começo da tabela, decidir sobre a volta
// pedia ler trinta colunas à direita e voltar. O grupo leva o nome de quem decide.
const COL_IMPORTAR = {
    ida:     { key: '__importarIda', label: 'Importar', group: 'rsvpIda',   selecao: true, lado: 'ida' },
    retorno: { key: '__importarRet', label: 'Importar', group: 'rsvpVolta', selecao: true, lado: 'retorno' },
};
// Colunas de um LADO do voo — é o que faz a linha inteira daquele lado responder ao clique e
// vestir a marcação, e não só o quadradinho de 13px no meio da planilha.
const LADO_DO_GRUPO = {
    rsvpIda: 'ida', percursoIda: 'ida', ida: 'ida',
    rsvpVolta: 'retorno', percursoVolta: 'retorno', retorno: 'retorno',
};

// colspans calculados dinamicamente; grupos sem coluna visível são omitidos no render
const GROUPS = [
    { key: null,            label: '' },
    { key: 'rsvpIda',       label: 'RSVP' },
    { key: 'percursoIda',   label: 'Percurso Ida' },
    { key: 'ida',           label: 'Ida' },
    { key: 'rsvpVolta',     label: 'RSVP' },
    { key: 'percursoVolta', label: 'Percurso Volta' },
    { key: 'retorno',       label: 'Retorno' },
    { key: 'valores',       label: 'Valores' },
    { key: 'pax',           label: 'PAX' },
    { key: 'obs',           label: 'Obs' },
];

const thGroup = { background: BRAND_NAVY, color: '#fff', letterSpacing: '0.08em', borderColor: BRAND_BORDER };
const thSub   = { background: BRAND_SKY,  color: BRAND_NAVY, letterSpacing: '0.06em', borderColor: BRAND_BORDER };

// Tom de fundo por status de verificação da célula (só nas colunas verificadas,
// com o painel Trechos aberto). Verde = casa · amarelo = diverge · vermelho = falta.
const CELL_TINT  = { ok: 'rgba(16,185,129,0.16)', divergente: 'rgba(245,158,11,0.18)', faltando: 'rgba(239,68,68,0.16)' };
const VERIF_TITULO = { ok: 'Confere com o trecho', divergente: 'Diverge do trecho', faltando: 'Faltando na opção' };
// Quanto as linhas focadas pelo painel de trechos piscam, em ms: a animação e a limpeza do foco.
const DURACAO_FOCO = 2600;
// Coluna → campo verificado: qual status de verificarColunas() tinge esta célula.
// Origem/destino têm várias colunas (IATA/Cidade/Aero., compacto e percurso) — todas
// pintam pelo mesmo status do lado, IATA incluída: o casador compara sigla com sigla e, pela
// tabela de metrópoles, com o nome do trecho. Colunas fora deste mapa ficam sempre brancas.
const COL_VERIF = {
    idaOrigemCidade: 'origemIda', idaOrigemAeroporto: 'origemIda',
    p_idaOrigemIata: 'origemIda', p_idaOrigemCidade: 'origemIda', p_idaOrigemAeroporto: 'origemIda',
    idaDestinoAeroporto: 'destinoIda',
    p_idaDestinoIata: 'destinoIda', p_idaDestinoCidade: 'destinoIda', p_idaDestinoAeroporto: 'destinoIda',
    idaData: 'dataIda',
    idaSaida: 'saidaIda', p_idaSaida: 'saidaIda',
    idaChegada: 'chegadaIda', p_idaChegada: 'chegadaIda',
    retOrigemAeroporto: 'origemRet',
    p_retOrigemIata: 'origemRet', p_retOrigemCidade: 'origemRet', p_retOrigemAeroporto: 'origemRet',
    retDestinoAeroporto: 'destinoRet',
    p_retDestinoIata: 'destinoRet', p_retDestinoCidade: 'destinoRet', p_retDestinoAeroporto: 'destinoRet',
    retData: 'dataRet',
    // Horas do retorno: conferidas desde que o trecho ganhou saída e chegada da volta.
    retSaida: 'saidaRet', retChegada: 'chegadaRet',
};
// Colunas mescladas (rowspan por valor) entre as opções de uma mesmo trecho.
export const MERGE_KEYS = ['idaOrigemCidade', 'tktPessoa', 'tktGrupo', 'numParticipantes', 'obs'];
// Colunas CALCULADAS (read-only): espelham as fórmulas do export Modelo Cliente —
// total por opção, TKT por pessoa e por grupo (estes dois, valor único do grupo).
export const COLS_CALCULADAS = ['total', 'tktPessoa', 'tktGrupo'];

// Coluna compacta do aeroporto → o campo da cidade que vai fixo ao lado dele, "(Natal) São Gonçalo do
// Amarante": o RSVP nem sempre sabe de que cidade é um aeroporto pelo nome (05/10).
const CIDADE_DO_AEROPORTO = {
    idaOrigemAeroporto: 'idaOrigemCidade', idaDestinoAeroporto: 'idaDestinoCidade',
    retOrigemAeroporto: 'retOrigemCidade', retDestinoAeroporto: 'retDestinoCidade',
};
// As classes do campo de cada célula. A cidade na frente do aeroporto e a medida dele usam as mesmas,
// para terem a fonte, a altura de linha e o padding do campo, venham de onde vierem.
const CLASSE_DO_CAMPO = 'form-control form-control-sm border-0 bg-transparent shadow-none';

// Teto do resize manual = largura máxima do export Modelo Cliente (60 unidades
// Excel ≈ 425px — a coluna OBS no auto-fit do .xlsx). Só UI, nada persiste.
const LARGURA_MAX_COL = 425;
// A coluna de importar é medida em PIXELS, com o texto medido de fato na fonte da planilha (o
// canvas do `larguraCh`). A estimativa em `ch` das outras colunas não serve aqui: as três peças
// são escritas em corpo menor que o da tabela (11, 12 e 10px contra 14), e `ch` é a largura do
// ZERO, mais largo que a minúscula média. A conta saía sempre perto dos 140px fixos de antes, e a
// coluna não mudava com o conteúdo.
//
// O corpo e o peso de cada peça moram aqui e o JSX lê daqui: mudar a fonte de uma delas sem mudar
// a medida deixaria a coluna cortando o texto ou sobrando de novo.
const FONTE_TODAS = { tamanho: 11, peso: 600 };      // marca-tudo do cabeçalho
const FONTE_IMPORTAR = { tamanho: 12, peso: 400 };   // rótulo do checkbox da célula
const FONTE_BADGE_LADO = { tamanho: 10, peso: 600 }; // badge de estado do lado
const ROTULO_TODAS = { ida: 'Todas as idas', retorno: 'Todas as voltas' };
// O que não é texto, tirado do CSS de cada peça:
const CAIXA_CHECKBOX = 16 + 4;       // .fin-pp__check (16px) + o gap-1 até o rótulo
const PADDING_CABECALHO = 4 + 4;     // th do .table-sm
const PADDING_CELULA = 8 + 8;        // o invólucro da célula (o td zera o próprio)
const CAIXA_BADGE = 9 + 9 + 6 + 5;   // .fin-pp__pill: padding, a bolinha do ::before e o gap
const FOLGA_BORDA = 2;               // a borda da célula e o arredondamento da medida
// O véu do lado marcado para importar: uma sombra azul rente às bordas, branca no meio.
//
// O raio é UM só e em PIXELS, e é o que mantém a borda de cima, a de baixo e as duas laterais
// com a mesma grossura. Em porcentagem cada uma media contra uma dimensão diferente — a
// lateral, contra a largura da coluna — e numa célula larga ela saía várias vezes mais grossa.
//
// `VEU_CURVA` é o que faz isso parecer BLUR e não rampa: [distância, peso do alfa]. Uma sombra
// desfocada cai depressa perto da borda e arrasta uma cauda longa; um gradiente de duas paradas
// cai em linha reta e o olho lê a borda dura. As paradas abaixo aproximam a primeira curva.
// A cor zero é a MESMA cor com alfa 0, e não `transparent`: interpolar até `transparent` passa
// por preto transparente e deixa a franja cinzenta.
const VEU_PX    = 14;
const VEU_ALFA  = 0.16;
const VEU_CURVA = [[0, 1], [0.22, 0.5], [0.5, 0.2], [0.75, 0.07], [1, 0]];
// O texto do lado marcado é o NAVY da tela, e não um azul próprio: a célula comum herda o cinza
// do .form-control, então o navy já é a diferença — mais escuro e mais azul, sem inventar um tom
// que não existe em lugar nenhum. O azul de link da marca competia com os dados em vez de dizer
// só que aquilo está escolhido.
const VEU_TEXTO = BRAND_NAVY;
const veuAzul = (peso) => `rgba(45, 127, 249, ${+(VEU_ALFA * peso).toFixed(4)})`;
// As paradas de uma borda, da borda para dentro (`aFrente`) ou de dentro para a borda.
const veuParadas = (aFrente) => (aFrente
    ? VEU_CURVA.map(([d, p]) => `${veuAzul(p)} ${(d * VEU_PX).toFixed(2)}px`)
    : [...VEU_CURVA].reverse().map(([d, p]) => `${veuAzul(p)} calc(100% - ${(d * VEU_PX).toFixed(2)}px)`)
).join(', ');
// Quanto um texto ocupa, em unidades `ch`. NÃO é a contagem de caracteres: `ch` é a largura do
// algarismo ZERO, e letra maiúscula é bem mais larga que ele — "LUFTHANSA" são nove caracteres e
// ocupa perto de 10,8ch. Medindo um a um, a largura automática reservava 9 e o nome da cia em
// caixa alta saía cortado, que é o contrário do que ela existe para fazer. Dois pesos bastam:
// isto é estimativa para dimensionar coluna, não métrica de fonte.
const PESO_MAIUSCULA = 1.2;
const larguraEmCh = (texto) => {
    let n = 0;
    for (const c of String(texto ?? '')) {
        n += (c !== c.toLowerCase() && c === c.toUpperCase()) ? PESO_MAIUSCULA : 1;
    }
    return n;
};
// Arraste de coluna com o cursor encostado na borda direita: quanto a largura ganha por passo, em
// px de tela, e de quanto em quanto tempo. Dá ~130px/s. Em 14 — que é o passo de um auto-scroll de
// navegação — a coluna ia do mínimo ao teto de 425 em menos de meio segundo, e não havia como parar
// no tamanho que se queria.
const PASSO_BORDA = 4;
const INTERVALO_BORDA = 30;
// Faixa junto à borda direita que dispara o avanço. Estreita de propósito: em 30px, afastar o mouse
// da borda para DIMINUIR a coluna ainda caía dentro dela, e a coluna continuava crescendo contra o
// gesto — era isso que fazia o encolher parecer emperrado.
const FAIXA_BORDA = 6;

// Zoom da tabela pela propriedade `zoom` do CSS. O mínimo NÃO é fixo: é o fator em que a
// planilha cabe inteira na largura visível da rolagem (medido em `zoomMinimo`) — abaixo dele
// sobraria espaço vazio à direita. O piso só cobre a medida degenerada (tabela gigante).
// Não há teto: o zoom do navegador já leva o conjunto além de qualquer número que se fixasse
// aqui, e o campo do % limita sozinho (3 dígitos). O piso segue, para a medida degenerada.
const ZOOM_PISO = 0.3;
const ZOOM_PASSO = 0.1;          // grade do clique: vai para o múltiplo seguinte (53 -> 60, 53 -> 50)
const ZOOM_PASSO_SEGURA = 0.01;  // segurando: 1 ponto percentual por vez, contínuo
// Os dois botões de zoom. `WebkitAppRegion: no-drag` porque, numa janela em que a faixa em volta
// conta como área de arrastar a janela, o botão herdaria isso: o pressionar viraria início de
// arraste da janela e o segurar nunca começava.
const ZOOM_BOTAO = {
    background: '#fff', border: `1px solid ${BRAND_BORDER}`, color: BRAND_NAVY,
    borderRadius: 8, padding: '4px 8px', WebkitAppRegion: 'no-drag',
};
const ZOOM_ESPERA = 300;         // ms segurando antes de começar a repetir
const ZOOM_INTERVALO = 50;       // ms entre os passos do segurar (1pp/50ms = 20pp por segundo)
const CHAVE_ZOOM = 'planilhaMalhaAerea.zoom';
const lerZoom = () => {
    try {
        const z = Number(localStorage.getItem(CHAVE_ZOOM));
        return z > 0 ? z : 1;
    } catch (e) {
        return 1;
    }
};

// Altura de um elemento fixo da moldura (navbar do topo, rodapé de botões). Zero quando ele
// não existe ou está no fluxo — aí ele não come espaço da janela.
const alturaFixaDe = (seletor) => {
    const el = document.querySelector(seletor);
    if (!el) return 0;
    const pos = getComputedStyle(el).position;
    return pos === 'fixed' || pos === 'sticky' ? el.getBoundingClientRect().height : 0;
};

// Largura de 1ch (glifo "0") na fonte do elemento — para comparar o auto (em ch) com o
// override manual (em px) sem heurística de fonte. Canvas único, reusado.
let medidorCtx = null;
const larguraCh = (el) => {
    if (!medidorCtx) medidorCtx = document.createElement('canvas').getContext('2d');
    const cs = getComputedStyle(el);
    medidorCtx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    return medidorCtx.measureText('0').width;
};
// Largura de um texto em PIXELS, no corpo e no peso pedidos, na família da planilha. Mesmo canvas
// do `larguraCh`. Sem canvas — o jsdom dos testes não tem — sai uma estimativa grosseira: ali a
// coluna só precisa existir.
const larguraTexto = (texto, { tamanho, peso }, familia) => {
    if (!medidorCtx) medidorCtx = document.createElement('canvas').getContext('2d');
    if (!medidorCtx) return String(texto).length * tamanho * 0.6;
    medidorCtx.font = `${peso} ${tamanho}px ${familia}`;
    return medidorCtx.measureText(String(texto)).width;
};

// Cabeçalho da coluna de importar: o marca-tudo daquele lado. O rótulo "Importar" fica na célula,
// junto do checkbox de cada opção; aqui em cima o que serve é a ação de uma vez só.
function CabecalhoSelecao({ lado, resumo, onAlternarTodos }) {
    const r = resumo ?? { podem: 0, marcados: 0 };
    const todos = r.podem > 0 && r.marcados === r.podem;
    const nome = lado === 'ida' ? 'ida' : 'volta';
    return (
        <label className="d-flex align-items-center justify-content-center gap-1 mb-0"
            style={{ textTransform: 'none', letterSpacing: 0, fontSize: FONTE_TODAS.tamanho,
                fontWeight: FONTE_TODAS.peso, cursor: r.podem ? 'pointer' : 'default' }}
            title={`Marca ou desmarca a ${nome} de todas as opções que podem ser importadas`}>
            <input type="checkbox" className="fin-pp__check" checked={todos} disabled={!r.podem}
                onChange={() => onAlternarTodos(lado, !todos)} />
            {ROTULO_TODAS[lado]}
        </label>
    );
}

// Endpoints IATA de um lado — conexões ('\n') usam a 1ª origem e o último destino.
const rotaLado = (op, lado) => {
    const o = String(op[`${lado}OrigemIata`] ?? '').split('\n')[0].trim();
    const dLinhas = String(op[`${lado}DestinoIata`] ?? '').split('\n');
    const d = dLinhas[dLinhas.length - 1].trim();
    return (o || d) ? { o: o || '?', d: d || '?' } : null;
};

// Linhas da coluna de rota: ida e volta ESPELHADAS (mesma rota invertida) viram uma
// linha só "SAO ⇄ CWB"; diferentes (ou sem retorno) ficam separadas com "➝".
const rotasControl = (op) => {
    const ida = rotaLado(op, 'ida');
    const ret = rotaLado(op, 'ret');
    if (ida && ret && ida.o === ret.d && ida.d === ret.o) return [`${ida.o} ⇄ ${ida.d}`];
    const linhas = [];
    if (ida) linhas.push(`${ida.o} ➝ ${ida.d}`);
    if (ret) linhas.push(`${ret.o} ➝ ${ret.d}`);
    return linhas;
};


/**
 * @param {string}   titulo           título do card
 * @param {node}     acoes            botões do cabeçalho do card, de quem usa a tabela
 * @param {Array}    opcoes           opções com `_id` (ver `prepara` na tela da malha)
 * @param {Array}    trechos          vínculos da malha, na ordem da tela
 * @param {boolean}  editavel         campos editáveis (o Aéreo, na tela da malha)
 * @param {Function} onEditar         (indices, campo, valor) — célula comum
 * @param {Function} onEditarMesclado (indices, campo, valor) — célula mesclada
 * @param {boolean}  coresAtivas      tinge as células conferidas contra o trecho
 * @param {object}   selecao          { lados(op) → { ida, retorno }, onAlternar(op, lado),
 *                                    onAlternarTodos(lado, marcar) }. Cada lado:
 *                                    { tem, pode, marcado, rotulo, titulo }
 * @param {number}   alturaMaxima     altura máxima da rolagem; sem ela, a da janela
 * @param {Array}    medirDeNovo      valores que mudam o que fica fixo na página (rodapé)
 * @param {Function} onExcluir        (ids) — os `_id` das linhas marcadas para excluir
 * @param {Function} motivoNaoExcluir (op) → por que a linha não sai do Excluir, ou null
 * @param {Function} onAlterarOpcoes  ([{ i, campos }]) — vários campos em várias linhas de uma vez:
 *                                    o destaque e o colar do clique direito
 * @param {Function} onDuplicar       (novas) — o Duplicar do clique direito: a cópia como linhas novas, no
 *                                    formato do `createEmptyOpcao`, para a página relacionar e pôr na malha
 * @param {Set}      importadas       ids das opções que estão nas opções do evento: o Colar nelas pede
 *                                    confirmação
 * @param {Object}   foco             { ids, vez } — as opções que o painel de trechos pediu para focar (`_id`).
 *                                    `vez` muda a cada pedido, para o mesmo vínculo poder ser focado de novo
 */
export default function PlanilhaMalhaAerea({
    titulo, acoes = null, opcoes, trechos, editavel = false, onEditar, onEditarMesclado,
    coresAtivas = false, selecao = null, alturaMaxima = null, medirDeNovo = [],
    onExcluir = null, motivoNaoExcluir = null, onAlterarOpcoes = null, onDuplicar = null, importadas = null, foco = null,
}) {
    const [detalhes, setDetalhes] = useState(false);
    // A célula da Origem do modo cliente em edição (o índice da opção): com o foco, ela mostra a conexão inteira.
    const [origemEditando, setOrigemEditando] = useState(null);
    const [colWidths, setColWidths] = useState({});
    const [zoom, setZoom] = useState(lerZoom);
    // Espelhos em ref: o ResizeObserver e o intervalo do "segurar" foram criados num render
    // anterior e leriam o valor velho se dependessem do closure.
    const zoomRef = useRef(zoom);
    zoomRef.current = zoom;
    const tabelaRef = useRef(null);
    const zoomMinimoRef = useRef(ZOOM_PISO);
    const [zoomMinimo, setZoomMinimo] = useState(ZOOM_PISO);

    const [digitandoZoom, setDigitandoZoom] = useState(null);

    // Em centésimos: em fração, 0.1 + 0.02 já dá 0.12000000000000001 e a grade do clique erra.
    // No mínimo, porém, vale o fator medido CRU (4 casas): ele é o encaixe exato na largura, e
    // arredondar para 1% devolveria a sobra que ele existe para tirar. O rótulo continua
    // inteiro (40%), só o zoom aplicado é que é preciso.
    const limitarZoom = (valor) => {
        const min = zoomMinimoRef.current;
        const novo = valor <= min ? min : Math.round(valor * 100) / 100;
        try { localStorage.setItem(CHAVE_ZOOM, String(novo)); } catch (e) { /* só conveniência */ }
        return novo;
    };
    const mudarZoom = (delta) => setZoom(atual => limitarZoom(atual + delta));
    // O clique não soma o passo: pula para o múltiplo seguinte da grade. Vindo de um valor
    // quebrado (o segurar, ou o mínimo medido), 53% vai para 60% no + e para 50% no -.
    const passoZoom = (sinal) => setZoom(atual => {
        const grade = Math.round(ZOOM_PASSO * 100);
        const p = Math.round(atual * 100);
        const alvo = sinal > 0 ? (Math.floor(p / grade) + 1) * grade : (Math.ceil(p / grade) - 1) * grade;
        return limitarZoom(alvo / 100);
    });
    const confirmarZoomDigitado = () => {
        const texto = String(digitandoZoom).replace(/\D/g, '');
        setDigitandoZoom(null);
        // Campo vazio cancela; 0 (ou qualquer valor abaixo) cai no mínimo pelo próprio limitador,
        // que é o encaixe medido na largura — é o atalho para "encaixa aí".
        if (texto !== '') setZoom(() => limitarZoom(Number(texto) / 100));
    };

    // Segurar o botão anda em passo curto e contínuo. O clique que fecha o segurar é engolido
    // (`repetiu`), senão o passo do clique viraria um pulo extra em cima do que já andou.
    const segurando = useRef({ espera: null, repete: null, repetiu: false });
    const pararZoom = () => {
        clearTimeout(segurando.current.espera);
        clearInterval(segurando.current.repete);
        segurando.current.espera = null;
        segurando.current.repete = null;
    };
    const segurarZoom = (sinal) => {
        pararZoom();
        segurando.current.repetiu = false;
        segurando.current.espera = setTimeout(() => {
            segurando.current.repete = setInterval(() => {
                segurando.current.repetiu = true;
                mudarZoom(sinal * ZOOM_PASSO_SEGURA);
            }, ZOOM_INTERVALO);
        }, ZOOM_ESPERA);
        // Soltar em QUALQUER lugar encerra, e é só isto que encerra. O botão pode ficar `disabled`
        // no meio do segurar (chegou no limite) e aí ele não recebe mais o pointerup; e o ponteiro
        // pode sair de cima dele sem que o dedo tenha soltado — antes havia um `onPointerLeave` que
        // tratava isso como fim, e bastava a mão tremer para o segurar cortar.
        window.addEventListener('pointerup', pararZoom, { once: true });
        window.addEventListener('pointercancel', pararZoom, { once: true });
    };
    const clicarZoom = (sinal) => {
        if (segurando.current.repetiu) { segurando.current.repetiu = false; return; }
        passoZoom(sinal);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => pararZoom, []);

    // Colunas visíveis conforme o modo (compacto x detalhado) + colspan por grupo
    const visibleCols = useMemo(() => {
        const base = COLS.filter(c => c.detail ? detalhes : (c.compactOnly ? !detalhes : true));
        if (!selecao) return base;
        // Antes da PRIMEIRA coluna do lado — o percurso no modo detalhado, o bloco compacto no
        // outro. Pelo grupo, e não por uma chave fixa: assim a coluna acompanha o modo sozinha.
        const antesDe = (lista, grupos, col) => {
            const i = lista.findIndex(c => grupos.includes(c.group));
            return i < 0 ? lista : [...lista.slice(0, i), col, ...lista.slice(i)];
        };
        return antesDe(
            antesDe(base, ['percursoIda', 'ida'], COL_IMPORTAR.ida),
            ['percursoVolta', 'retorno'], COL_IMPORTAR.retorno,
        );
    }, [detalhes, selecao]);
    const groupCols = (gkey) => visibleCols.filter(c => c.group === gkey).length;

    // Rolagem vertical: a altura é a da janela menos a barra FIXA do topo e o maior de dois
    // pisos embaixo — o rodapé fixo de botões, ou o espaço que a página tem depois da tabela.
    // Medido, não chutado: o rodapé muda com os botões de cada cargo e quebra em duas linhas
    // em tela estreita. Com `alturaMaxima`, vale ela (várias planilhas na mesma página).
    const [alturaTabela, setAlturaTabela] = useState(null);
    const [espacoFinal, setEspacoFinal] = useState(0);
    const areaTabelaRef = useRef(null);
    const temOpcoes = opcoes.length > 0;
    useEffect(() => {
        if (alturaMaxima) return undefined;
        const medir = () => {
            // 12px de folga para a borda do card não encostar na sombra do rodapé.
            const rodape = alturaFixaDe('.fixoRodape') + 12;
            // Do fim da rolagem ao fim do CARD (borda + margem), NÃO ao fim do `#main`: o
            // espaçador do ErpTemplate vem depois e é medido à parte, abaixo.
            const area = areaTabelaRef.current;
            const cartao = area ? area.parentElement : null;
            const depois = cartao
                ? cartao.getBoundingClientRect().bottom - area.getBoundingClientRect().bottom
                    + (parseFloat(getComputedStyle(cartao).marginBottom) || 0)
                : 0;
            const reserva = Math.max(rodape, depois);
            // 240px de piso para a tabela não virar uma fresta em janela baixa.
            setAlturaTabela(Math.max(240, Math.round(
                window.innerHeight - alturaFixaDe('.navbar-header') - reserva
            )));
            // Depois do card, a página precisa ter EXATAMENTE a reserva — nem mais, nem menos.
            // Menos, e falta rolagem para o fim da tabela subir acima do rodapé fixo; mais, e a
            // rolagem sobra: o topo da tabela entra atrás do navbar e o excedente abre um vão
            // embaixo. É por isso que a tela desliga o espaçador chutado do ErpTemplate
            // (`espacoFinal={0}`) e a conta vem daqui, do rodapé medido.
            setEspacoFinal(Math.max(0, Math.round(reserva - depois)));
            ajustes.current = 0;   // geometria nova: o ajuste fino abaixo pode trabalhar de novo
        };
        medir();
        window.addEventListener('resize', medir);
        const rodape = document.querySelector('.fixoRodape');
        const observador = rodape && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
        if (observador) observador.observe(rodape);
        return () => {
            window.removeEventListener('resize', medir);
            if (observador) observador.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alturaMaxima, temOpcoes, ...medirDeNovo]);

    // Mínimo de zoom = o fator em que a planilha encosta EXATAMENTE na largura visível da
    // rolagem: sem barra horizontal e sem sobra à direita. Não dá para extrapolar da largura
    // natural, porque o arredondamento de cada coluna muda com o zoom (medido: 1863px de
    // layout a 100% viram 1878px a 50%) — então a conta sai do render de AGORA e, se ainda
    // sobrar mais de 1px, a medida seguinte do observador refina. O mínimo fica com 4 casas:
    // arredondá-lo para 1% deixaria até ~1% da largura vazia, que é o oposto do objetivo.
    // `vezes` limita o refino: a barra vertical pode ir e vir com a altura e a conta ficaria
    // indo e voltando sozinha.
    const refino = useRef({ visivel: 0, vezes: 0 });
    // A medida do encaixe, para o fim do arraste de coluna poder pedi-la. Ela mora dentro do efeito
    // (fecha sobre os refs e os setters) e o arraste vive fora dele.
    const medirZoomRef = useRef(null);
    useEffect(() => {
        const area = areaTabelaRef.current;
        const tabela = tabelaRef.current;
        if (!area || !tabela || typeof ResizeObserver === 'undefined') return undefined;
        // `forcarEncaixe`: quem chama sabe que o zoom estava no encaixe antes de a largura mudar.
        // Só o fim do arraste de coluna usa — ver o `onUp` do `startResize`.
        const medir = (forcarEncaixe = false) => {
            const visivel = area.clientWidth;
            const renderizado = tabela.getBoundingClientRect().width;
            const zoomAtual = zoomRef.current || 1;
            if (visivel < 2 || renderizado < 1) return;
            if (refino.current.visivel !== visivel) refino.current = { visivel, vezes: 0 };
            const folga = visivel - renderizado;  // > 0 sobra à direita, < 0 estoura (barra)
            const noMinimo = zoomAtual <= zoomMinimoRef.current + 0.0005;
            if (noMinimo) {
                // Assimétrico de propósito: até 1px de sobra está encaixado, mas estouro
                // NENHUM — sobrar 1px não se vê, a barra horizontal se vê.
                if (folga >= 0 && folga <= 1) return;
                // Teto do refino: a barra vertical vai e vem com a altura, e sem teto a
                // conta ficaria se corrigindo sozinha para sempre. O fim do arraste não é refino:
                // é uma medida pedida, uma só, e não pode esbarrar no orçamento de outra coisa.
                if (!forcarEncaixe && refino.current.vezes >= 8) return;
                refino.current.vezes += 1;
            }
            // Sem teto em 1: com a tela larga (ou o zoom do navegador afastado) a planilha cabe
            // com folga, e o encaixe exato passa de 100% — é o mínimo mesmo assim.
            const anterior = zoomMinimoRef.current;
            const min = Math.max(ZOOM_PISO, Math.round(zoomAtual * (visivel / renderizado) * 10000) / 10000);
            if (Math.abs(min - anterior) >= 0.0002) {
                zoomMinimoRef.current = min;
                setZoomMinimo(min);
            }
            // Quem estava encaixado segue encaixado: o mínimo refinado leva o zoom com ele nos
            // dois sentidos. Sem a segunda condição, refinar o mínimo PARA BAIXO deixava o zoom
            // acima dele — e o estouro que o refino acabou de calcular continuava na tela.
            //
            // Vale TAMBÉM durante o arraste de uma coluna: alargando uma coluna com a planilha
            // encaixada, o zoom desce junto e ela continua cabendo na tela — é o comportamento de
            // "encaixar na largura", e ver o resultado enquanto se arrasta é o que se quer. Quem
            // arrasta em px de tela não sente a diferença porque a conversão do arraste lê o zoom
            // do momento, não o do começo (ver `startResize`).
            //
            // Isto fica FORA do "o mínimo mudou": ele pode chegar ao valor final antes de o zoom
            // chegar, e aí a medida seguinte sairia por aquele atalho sem nunca aplicar. Repetir o
            // mesmo valor não custa — o React descarta o set igual.
            //
            // O `forcarEncaixe` é o acerto final, pedido pelo `onUp` do arraste: ali a comparação
            // com o `anterior` pode já não valer, porque o mínimo andou durante o gesto.
            setZoom(atual => (forcarEncaixe || atual <= min || atual <= anterior + 0.0005 ? min : atual));
        };
        medirZoomRef.current = medir;
        medir();
        // Envelopado, e não `medir` direto: o observador entrega as entradas e o resize entrega o
        // Event, e qualquer um dos dois chegaria como `forcarEncaixe` truthy — o zoom voltaria ao
        // encaixe a cada medida, desfazendo o que o usuário tivesse ampliado.
        const aoMudar = () => medir();
        const observador = new ResizeObserver(aoMudar);
        observador.observe(area);
        observador.observe(tabela);
        // O observador já pega a largura mudando (janela, colunas arrastadas, modo detalhado),
        // mas o zoom do NAVEGADOR pode mudar a janela sem mexer na caixa — daí o resize também.
        window.addEventListener('resize', aoMudar);
        return () => {
            observador.disconnect();
            window.removeEventListener('resize', aoMudar);
            medirZoomRef.current = null;
        };
    }, []);

    // Arrastar uma coluna muda a largura natural da planilha e, com ela, o encaixe: devolve o
    // orçamento de refino para o mínimo ser recalculado do zero na medida seguinte.
    useEffect(() => { refino.current = { visivel: 0, vezes: 0 }; }, [colWidths, visibleCols]);

    // Ajuste fino do espaço final, na régua: a conta acima erra por alguns px (borda, colapso de
    // margem, barra de rolagem) e o erro vira rolagem sobrando — a planilha passa do topo e o
    // mesmo tanto abre de vão embaixo. Aqui mede quanto a página rola ALÉM do necessário para a
    // planilha encostar no topo e desconta. Converge no primeiro passo e para quando zera; o teto
    // existe porque a sobra pode vir de fora do componente, e aí não há o que descontar.
    const ajustes = useRef(0);
    useLayoutEffect(() => {
        const area = areaTabelaRef.current;
        if (alturaMaxima || !area || ajustes.current >= 6) return;
        const precisa = area.getBoundingClientRect().top + window.scrollY - alturaFixaDe('.navbar-header');
        const sobra = Math.round((document.documentElement.scrollHeight - window.innerHeight) - precisa);
        // SÓ desconta. Sobra negativa é falta de rolagem — planilha curta, página que nem precisa
        // rolar — e inflar o espaço final para "fabricar" rolagem enche a tela de vão vazio.
        // O piso de 2px é ruído de arredondamento: `scrollHeight` e `innerHeight` são inteiros e
        // o retângulo é fracionário, e com zoom do navegador a diferença é de 1px.
        if (sobra < 2) return;
        ajustes.current += 1;
        setEspacoFinal(atual => Math.max(0, atual - sobra));
    }, [espacoFinal, alturaTabela, alturaMaxima]);

    // Rolagem vertical em cascata: UM DONO POR TIQUE, nunca os dois.
    //
    // A versão anterior repartia cada tique entre a página e a tabela dentro do mesmo evento. A
    // conta está certa (medida em réplica: descendo, a sequência página → tabela → página anda sem
    // falhar), mas no navegador o gesto real travava: o Chrome escolhe o alvo da rolagem quando o
    // gesto começa e prende nele, e trocar de dono no meio obrigava a soltar a roda e recomeçar.
    //
    // Agora, a cada tique, ou a tabela leva tudo (e o tique some para o navegador) ou nada é
    // interceptado e a rolagem é a nativa da página, com inércia, barra e tudo. Quem decide é a
    // POSIÇÃO, perguntada de novo em cada tique — não um estado guardado, que trava assim que uma
    // condição de entrada deixa de acontecer.
    //
    // O ouvinte é da JANELA, não da caixa: a tabela rola com o cursor em qualquer lugar da tela.
    // O horizontal não passa por aqui — é o nativo da caixa, intocado.
    useEffect(() => {
        const area = areaTabelaRef.current;
        if (!area || alturaMaxima) return undefined;
        // A página acabou. Vale como "a planilha está no lugar" mesmo que ela não tenha encostado no
        // topo: acima da caixa ainda há o cabeçalho do cartão, e se a rolagem da página terminar
        // antes de ele passar sob o navbar, exigir o encosto exato deixaria a tabela inalcançável.
        const paginaNoFim = () => {
            const el = document.scrollingElement || document.documentElement;
            return el.scrollHeight - el.clientHeight - el.scrollTop <= 1;
        };

        // A roda chega de qualquer lugar da tela. Estando o ponteiro sobre OUTRA caixa rolável que
        // ainda tem para onde ir — um modal, a coluna de um agrupamento, uma lista —, o gesto é
        // dela: a tabela não sequestra a rolagem dos outros por ser dona da cascata.
        const outroRolavel = (alvo, sentido) => {
            for (let el = alvo; el && el !== document.body; el = el.parentElement) {
                if (el === area) return false;
                const oy = getComputedStyle(el).overflowY;
                if (oy !== 'auto' && oy !== 'scroll') continue;
                const sobra = sentido > 0 ? el.scrollHeight - el.clientHeight - el.scrollTop : el.scrollTop;
                if (sobra > 1) return true;
            }
            return false;
        };

        const naRoda = (e) => {
            // Gesto lateral, zoom do navegador e shift+roda (que é rolagem lateral) não são nossos.
            if (e.ctrlKey || e.shiftKey || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
            // deltaMode: 0 = px, 1 = linhas, 2 = páginas.
            const passo = e.deltaMode === 1 ? e.deltaY * 16
                : e.deltaMode === 2 ? e.deltaY * window.innerHeight
                    : e.deltaY;
            if (!passo) return;
            if (!area.contains(e.target) && outroRolavel(e.target, passo)) return;
            const fim = area.scrollHeight - area.clientHeight;
            if (fim <= 0) return;                    // tabela inteira à vista: nada a rolar aqui
            const antes = area.scrollTop;

            // Quem é o dono sai da POSIÇÃO, não de um estado guardado. Estado latched trava: bastava
            // uma condição de entrada não acontecer — a planilha não encostar exatamente no topo, ou
            // o sentido ser o de subir — para a tabela nunca mais virar dona e parar de rolar.
            // Posição não trava: cada tique pergunta de novo, e a resposta acompanha a tela.
            //
            // DESCENDO, a tabela só assume depois de a planilha estar no lugar: antes disso, rolar é
            // trazer a planilha para a tela, e isso é da página.
            //
            // SUBINDO, o critério é outro: basta a tabela ter conteúdo escondido acima e estar bem à
            // vista. Exigir o mesmo "no lugar" abria uma faixa — a planilha parada logo abaixo do
            // topo, alcançável pela barra de rolagem ou pelo teclado — em que a roda para cima ia
            // para a página e a tabela ficava parada, com conteúdo rolado que ninguém conseguia
            // trazer de volta. É a metade que faltava do inverso da descida.
            const r = area.getBoundingClientRect();
            const topo = alturaFixaDe('.navbar-header');
            const aVista = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, topo);
            const bemAVista = aVista >= Math.min(r.height, window.innerHeight - topo) / 2;
            const meu = passo > 0
                ? (r.top - topo <= 1 || paginaNoFim()) && antes < fim
                : bemAVista && antes > 0;
            // Não sendo o tique dela, a tabela nem toca nele: ele segue para o navegador e a página
            // rola nativamente. Chegando à ponta, é assim que a devolução acontece — no mesmo tique,
            // sem espera e sem nada perdido.
            if (!meu) return;

            area.scrollTop = Math.max(0, Math.min(fim, antes + passo));
            if (area.scrollTop !== antes) e.preventDefault();
        };

        window.addEventListener('wheel', naRoda, { passive: false });
        return () => window.removeEventListener('wheel', naRoda);
    }, [alturaMaxima]);

    // O foco do painel de trechos: as linhas das opções piscam (`malha-foco`) e a planilha rola até a
    // primeira delas. A rolagem vale para a caixa e para a página juntas: a caixa tem `overflow-y:
    // hidden`, mas continua rolável por código, e a cascata acima decide o dono pela posição, então
    // ela segue de onde o foco deixou.
    const [focadas, setFocadas] = useState(() => new Set());
    useEffect(() => {
        if (!foco?.ids?.length) return undefined;
        setFocadas(new Set(foco.ids.map(String)));
        const fim = setTimeout(() => setFocadas(new Set()), DURACAO_FOCO);
        return () => clearTimeout(fim);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [foco?.vez]);
    useEffect(() => {
        if (!focadas.size) return;
        const linha = areaTabelaRef.current?.querySelector('tr.malha-foco');
        if (linha) linha.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [focadas]);

    // O cabeçalho da tabela gruda no topo da rolagem. São duas linhas (grupos e colunas), e
    // a segunda gruda logo abaixo da primeira — por isso precisa da altura dela. Em px da
    // própria tabela (`offsetHeight`), não da tela: com zoom, o `top` também é da tabela.
    const linhaGruposRef = useRef(null);
    const [alturaGrupos, setAlturaGrupos] = useState(0);
    useLayoutEffect(() => {
        setAlturaGrupos(linhaGruposRef.current ? linhaGruposRef.current.offsetHeight : 0);
    }, [temOpcoes, detalhes, zoom]);

    // O rodapé gruda no fim da rolagem, com as mesmas duas linhas do Modelo Cliente: a de cima
    // gruda logo acima da de baixo, então precisa da altura dela — em px da tabela, como o topo.
    const linhaTktMedioRef = useRef(null);
    const [alturaTktMedio, setAlturaTktMedio] = useState(0);
    useLayoutEffect(() => {
        setAlturaTktMedio(linhaTktMedioRef.current ? linhaTktMedioRef.current.offsetHeight : 0);
    }, [temOpcoes, detalhes, zoom]);

    // Largura mínima, em `ch`, que comporta o maior conteúdo ou o header. Usa `larguraEmCh` e não
    // o comprimento do texto: ver a nota lá em cima. O header é medido em CAIXA ALTA porque é assim
    // que ele aparece (`text-uppercase`).
    const charsAuto = (col) => {
        if (!col) return 10;
        if (col.control) {
            let n = larguraEmCh('SAO ⇄ CWB');
            for (const op of opcoes) {
                for (const linha of rotasControl(op)) n = Math.max(n, larguraEmCh(linha));
            }
            return n;
        }
        let n = larguraEmCh((col.label || '').toUpperCase());
        // No aeroporto, a cidade vai na frente ("Natal (São Gonçalo do Amarante)") e o aeroporto fica no
        // centro: a linha precisa do aeroporto e do espaço da cidade dos dois lados.
        const campoCidade = CIDADE_DO_AEROPORTO[col.key];
        for (const op of opcoes) {
            const txt = String(op[col.field || col.key] ?? '');
            const cidades = campoCidade ? cidadesDoAeroporto(op[campoCidade], txt) : null;
            for (const [i, linha] of txt.split('\n').entries()) {
                n = Math.max(n, cidades && cidades[i] ? larguraEmCh(linha) + 2 * larguraEmCh(`${cidades[i]} (`) : larguraEmCh(linha));
            }
        }
        return n;
    };

    // A largura da coluna de importar sai do CONTEÚDO, como a das outras: a maior das três peças —
    // o marca-tudo do cabeçalho, o checkbox com o rótulo, e o maior badge que ESTA malha mostra
    // naquele lado. Sem badge longo ela encolhe até o cabeçalho; com "Alterada no evento" ela
    // cresce o que ele pede, em vez de quebrar o badge em duas linhas.
    const larguraSelecao = (col) => {
        const familia = getComputedStyle(tabelaRef.current || document.body).fontFamily;
        const medir = (texto, fonte) => larguraTexto(texto, fonte, familia);
        const rotulos = new Set((opcoes ?? [])
            .map(op => (selecao?.lados(op) ?? {})[col.lado]?.rotulo)
            .filter(Boolean));
        return Math.ceil(Math.max(
            PADDING_CABECALHO + CAIXA_CHECKBOX + medir(ROTULO_TODAS[col.lado], FONTE_TODAS),
            PADDING_CELULA + CAIXA_CHECKBOX + medir('Importar', FONTE_IMPORTAR),
            ...[...rotulos].map(r => PADDING_CELULA + CAIXA_BADGE + medir(r, FONTE_BADGE_LADO)),
        ) + FOLGA_BORDA);
    };

    // Largura da COLUNA (só da coluna — o textarea da OBS tem largura própria, fixa no
    // teto, ver .malha-grow): override manual em px (resize sempre vale, nos 2 sentidos),
    // senão auto em ch; o auto da OBS satura no teto. Digitar solta o override quando o
    // conteúdo passa dele (soltarManualSeEstourar).
    const getW = (key) => {
        const selecionavel = [COL_IMPORTAR.ida, COL_IMPORTAR.retorno].find(c => c.key === key);
        if (selecionavel) return `${larguraSelecao(selecionavel)}px`;
        const manual = colWidths[key];
        if (manual != null) return `${manual}px`;
        const auto = `${charsAuto(COLS.find(c => c.key === key)) + 3}ch`; // +3 de folga
        return key === 'obs' ? `min(${auto}, ${LARGURA_MAX_COL}px)` : auto;
    };

    // Digitar solta o override manual da coluna SÓ quando o auto (conteúdo + a MESMA
    // folga +3ch do getW) passa dele — manual mais largo que o conteúdo continua
    // valendo. Vale para todas as colunas editáveis; a OBS depois satura no teto.
    const soltarManualSeEstourar = (col, valor, el) => {
        setColWidths(prev => {
            if (prev[col.key] == null) return prev;
            const chars = Math.max(
                charsAuto(col),
                ...String(valor).split('\n').map(larguraEmCh),
            ) + 3; // mesma medida e mesma folga do auto (getW)
            if (chars * larguraCh(el) <= prev[col.key]) return prev;
            const next = { ...prev };
            delete next[col.key];
            return next;
        });
    };

    const startResize = (e, key) => {
        e.preventDefault();
        // Estar no encaixe decide se, ao soltar, o zoom é reencaixado — é o sinal que o `onUp`
        // manda para a medida final.
        const estavaNoEncaixe = (zoom || 1) <= zoomMinimoRef.current + 0.0005;
        const th = e.target.closest('th');
        if (!th) return;
        const fatorAgora = () => zoomRef.current || 1;
        const noEncaixeAgora = () => (zoomRef.current || 1) <= zoomMinimoRef.current + 0.0005;

        // Contêiner de rolagem horizontal da tabela — é a área da tabela, sempre, e não o primeiro
        // ancestral que JÁ tenha overflow: no zoom mínimo a planilha cabe exata, ninguém tem
        // overflow ainda, e a busca antiga terminava sem scroller.
        const scroller = areaTabelaRef.current;
        let ultimoX = e.clientX;
        let autoScroll = null;

        // A barra horizontal fica ESCONDIDA durante o arraste. No encaixe, alargar a coluna abre
        // alguns pixels de overflow antes de o zoom acompanhar, e o zoom os fecha logo depois: a
        // barra aparecia e sumia a cada passo. `scrollLeft` continua valendo com `hidden`, então o
        // auto-scroll da borda (acima do encaixe) não perde nada.
        const overflowOriginal = scroller ? scroller.style.overflowX : null;
        if (scroller) scroller.style.overflowX = 'hidden';

        // A largura sai da POSIÇÃO DA BORDA, não de deslocamentos somados: a cada movimento, mede
        // onde a borda arrastada está na tela e acrescenta o que falta para ela alcançar o cursor.
        //
        // É o que dispensa o fator fixo. No encaixe, alargar derruba o zoom e parte do ganho volta
        // como redução — com deslocamento somado era preciso multiplicar por 2 para compensar, e
        // esse 2 era chute: ele não vale quando o arraste CRUZA o encaixe no meio do caminho, que
        // era a origem do "não é uniforme". Medindo a borda, a compensação é automática: se ela
        // ficou aquém do cursor, o passo seguinte cobre o que faltou, qualquer que seja o motivo.
        // Também não há acúmulo para escapar — a borda persegue o cursor e para onde ele está.
        //
        // `getBoundingClientRect` é px de TELA (o zoom já aplicado) e `offsetWidth` é px de TABELA:
        // é por isso que a diferença é dividida pelo zoom antes de virar largura.
        //
        // `avancoNaBorda` é o quanto a borda fica À DIREITA do cursor. Nasce com a distância do
        // ponto onde se pegou a alça até a borda — pegar a alça não pode encolher a coluna até o
        // cursor — e cresce quando o relógio da borda empurra a borda além do fim da tela.
        let avancoNaBorda = th.getBoundingClientRect().right - e.clientX;

        const aplicar = (clientX, passoMouse) => {
            const alvo = clientX + avancoNaBorda;
            const erro = alvo - th.getBoundingClientRect().right;
            // A correção é LIMITADA ao tamanho do próprio passo. Sem limite, a borda perseguiria o
            // cursor onde ele estivesse — e há um caso em que ela não pode alcançá-lo: a ÚLTIMA
            // coluna no encaixe. Ali a direita da tabela está presa na direita da caixa (é o que o
            // encaixe significa), a borda não sai do lugar por mais que a coluna cresça, o erro
            // nunca fecha, e corrigi-lo inteiro a cada evento faria a largura disparar sem nada se
            // mexer na tela. Com o limite, o pior caso é andar o dobro do passo — a borda alcança o
            // cursor em poucos quadros quando pode, e acompanha o mouse quando não pode.
            const teto = Math.abs(passoMouse);
            const anda = passoMouse + Math.max(-teto, Math.min(teto, erro));
            if (!anda) return;
            const nova = Math.min(LARGURA_MAX_COL, Math.max(40, th.offsetWidth + anda / fatorAgora()));
            setColWidths(prev => (prev[key] === nova ? prev : { ...prev, [key]: nova }));
        };

        const onMove = (ev) => {
            const xAnterior = ultimoX;
            ultimoX = ev.clientX;
            const passoMouse = ev.clientX - xAnterior;
            if (!scroller) { aplicar(ev.clientX, passoMouse); return; }
            // Encostado na borda direita → continua crescendo enquanto o mouse segura ali. Mas só
            // enquanto ele NÃO está voltando: qualquer movimento para a esquerda é intenção de
            // diminuir, e o avanço para no mesmo instante. Parado na borda, `clientX` se repete e o
            // `>=` mantém o crescimento.
            const naBorda = ev.clientX > scroller.getBoundingClientRect().right - FAIXA_BORDA
                && ev.clientX >= xAnterior;
            if (naBorda && !autoScroll) {
                autoScroll = setInterval(() => {
                    // NO ENCAIXE, NÃO SE ROLA DE LADO: não há para onde (a planilha cabe exata), e a
                    // tentativa só abriria o overflow que o zoom fecha em seguida. Acima do encaixe
                    // a planilha estoura a caixa de verdade, e rolar é como se alcança a última
                    // coluna; ali o próprio scroll afasta a borda do cursor e o `aplicar` cobre.
                    if (!noEncaixeAgora()) {
                        const antes = scroller.scrollLeft;
                        scroller.scrollLeft += PASSO_BORDA;
                        if (scroller.scrollLeft !== antes) { aplicar(ultimoX, PASSO_BORDA); return; }
                    }
                    // O passo do relógio entra no lugar do passo do mouse: parado na borda, o mouse
                    // não anda, e sem isto o limite da correção seria zero e nada cresceria.
                    avancoNaBorda += PASSO_BORDA;
                    aplicar(ultimoX, PASSO_BORDA);
                }, INTERVALO_BORDA);
            } else if (!naBorda && autoScroll) {
                clearInterval(autoScroll);
                autoScroll = null;
                // Saindo da borda, a borda da coluna está longe à direita do cursor — foi o relógio
                // que a levou. Voltar a colá-la no cursor a puxaria de volta num salto, então o
                // avanço é rebaseado na distância atual: nada se move agora, e a partir daqui o
                // mouse comanda de novo, um pixel por pixel, inclusive para encolher.
                avancoNaBorda = th.getBoundingClientRect().right - ev.clientX;
            }
            aplicar(ev.clientX, passoMouse);
        };
        const onUp = () => {
            if (autoScroll) clearInterval(autoScroll);
            // A barra volta a ser decidida pelo CSS: fora do arraste ela não pisca, e acima do
            // encaixe ela é a única indicação de que a planilha continua para a direita.
            if (scroller) scroller.style.overflowX = overflowOriginal;
            // Acerto final do encaixe, PEDIDO aqui: o observador só acorda quando algum tamanho
            // muda, e depois do último movimento do mouse nada mais muda. Sem isto, um resto de
            // diferença acumulado durante o gesto ficaria na tela até a próxima mexida.
            if (medirZoomRef.current) medirZoomRef.current(estavaNoEncaixe);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // Verificação por célula (origem/destino/datas vs trecho) — tinge SÓ as
    // colunas verificadas; o resto fica no estilo padrão.
    const verificacaoCelulas = useMemo(() => verificarColunas(opcoes, trechos), [opcoes, trechos]);

    // Opções agrupadas por VÍNCULO para o render da tabela (merge via rowspan).
    // Grupo avulso e trecho agem como um só aqui: a chave vem do `vinculoDe`,
    // e a ordem é a de `trechos`, que já carrega os dois na mesma lista.
    const gruposTabela = useMemo(() => {
        const byId = new Map();
        const orfas = [];
        opcoes.forEach((op, i) => {
            const sid = vinculoDe(op);
            if (sid) {
                if (!byId.has(sid)) byId.set(sid, []);
                byId.get(sid).push({ op, i });
            } else {
                orfas.push({ op, i });
            }
        });
        const grupos = [];
        trechos.forEach(s => {
            const rows = byId.get(String(s.id));
            if (rows && rows.length) grupos.push({ key: String(s.id), rows });
        });
        byId.forEach((rows, sid) => {
            if (!trechos.some(s => String(s.id) === sid)) grupos.push({ key: sid, rows });
        });
        orfas.forEach(r => grupos.push({ key: `orfa-${r.i}`, rows: [r] }));
        return grupos;
    }, [opcoes, trechos]);

    // Valor de uma coluna mesclável para fins de merge: o da opção, como a origem — valores
    // diferentes aparecem cada um no seu run, e o vazio adota o comum do grupo. Na OBS e no Num.
    // Part., o modal do grupo entra como mais uma linha nesse voto (`extrasDaMescla`).
    const valorMerge = (op, key) => {
        const col = COLS.find(c => c.key === key);
        return String(op[(col && col.field) || key] ?? '');
    };

    // Passageiros do bloco: o valor efetivo da primeira linha — o dela, ou o comum com o modal —,
    // que é o que o Salvar grava nela e o export lê (1ª ocorrência não vazia).
    const paxDoBloco = (grupo) => mesclarColuna(
        grupo.rows.map(({ op }) => valorMerge(op, 'numParticipantes')),
        extrasDaMescla(grupo.key, 'numParticipantes', trechos),
    )[0]?.value ?? '';

    // Rodapé com as fórmulas do rodapé do Modelo Cliente, sobre os mesmos blocos da tabela:
    //   TOTAL ESTIMADO = Σ TKT/grupo              (=SUM(W))
    //   participantes  = Σ passageiros dos blocos (=SUM(X))
    //   TKT MÉDIO      = total ÷ participantes    (=IF(X=0,0,U/X))
    // Passageiros do TKT do bloco = a primeira célula (`paxDoBloco`), como o W do export. O total de
    // participantes soma as células da coluna, uma por run, como o =SUM(X). Bloco sem passageiro
    // fica fora das duas somas (W = V×0).
    const totais = (() => {
        let estimado = 0;
        let participantes = 0;
        gruposTabela.forEach(grupo => {
            const ops = grupo.rows.map(({ op }) => op);
            estimado += tktPorGrupoNum(ops, Number(paxDoBloco(grupo)) || 0);
            participantes += mesclarColuna(
                ops.map(op => valorMerge(op, 'numParticipantes')),
                extrasDaMescla(grupo.key, 'numParticipantes', trechos),
            ).reduce((soma, run) => soma + (run ? (Number(run.value) || 0) : 0), 0);
        });
        return { estimado, participantes, medio: participantes ? estimado / participantes : 0 };
    })();

    // Uma linha do rodapé alinhada às colunas visíveis: o rótulo ocupa "Tarifa Ret." e "Taxas
    // Ret.", o valor fica em "Total" e os participantes em "Num. Part.", como no Modelo Cliente.
    const linhaRodape = (rotulo, valor, participantes, estiloLinha) => {
        const celulas = [];
        const base = { ...estiloLinha, background: BRAND_SKY, color: BRAND_NAVY, borderColor: BRAND_BORDER, fontSize: 13 };
        for (let i = 0; i < visibleCols.length; i++) {
            const col = visibleCols[i];
            // O rótulo cobre as duas colunas antes do Total: "Tarifa Ret." e "Taxas Ret." na visualização
            // completa, as duas de tarifa + taxas na compacta (Q:R do Modelo Cliente).
            if ((col.key === 'tarifaRet' && visibleCols[i + 1]?.key === 'taxasRet')
                || (col.key === 'tarifaTaxasIda' && visibleCols[i + 1]?.key === 'tarifaTaxasRet')) {
                celulas.push(
                    <td key={col.key} colSpan={2} className="align-middle text-center text-uppercase fw-bold" style={base}>
                        <div style={{ padding: '6px 8px', letterSpacing: '0.06em' }}>{rotulo}</div>
                    </td>
                );
                i++;
                continue;
            }
            const conteudo = col.key === 'total' ? 'R$ ' + moeda(valor)
                : (col.key === 'numParticipantes' && participantes !== null ? String(participantes) : '');
            celulas.push(
                <td key={col.key} className="align-middle text-center fw-bold" style={base}>
                    {conteudo && <div style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{conteudo}</div>}
                </td>
            );
        }
        return celulas;
    };

    // Cabeçalho da coluna Importar: marca ou desmarca de uma vez a ida (ou a volta) de tudo
    // que ainda pode ser importado.
    const resumoSelecao = useMemo(() => {
        if (!selecao) return null;
        const resumo = { ida: { podem: 0, marcados: 0 }, retorno: { podem: 0, marcados: 0 } };
        opcoes.forEach(op => {
            const lados = selecao.lados(op);
            ['ida', 'retorno'].forEach(lado => {
                if (lados?.[lado]?.pode) {
                    resumo[lado].podem++;
                    if (lados[lado].marcado) resumo[lado].marcados++;
                }
            });
        });
        return resumo;
    }, [opcoes, selecao]);

    // Todas as colunas de um lado respondem pelo checkbox daquele lado: clicar em qualquer célula
    // da ida marca a ida daquela opção, e marcada ela veste o azul. Aplicado por fora do
    // `renderCelula` — ele tem meia dúzia de saídas (controle, seleção, calculada, mesclada,
    // editável) e repetir a regra em cada uma é como elas divergem.
    // A primeira e a última coluna VISÍVEL de cada lado. É o que dá começo e fim à faixa: o miolo
    // é tinta lisa, e só as pontas curvam. Depende do modo (o percurso entra e sai), por isso sai
    // das colunas visíveis e não de uma lista fixa.
    const pontasDoLado = useMemo(() => {
        const m = {};
        visibleCols.forEach(c => {
            const lado = LADO_DO_GRUPO[c.group];
            if (!lado) return;
            if (!m[lado]) m[lado] = { ini: c.key, fim: c.key };
            else m[lado].fim = c.key;
        });
        return m;
    }, [visibleCols]);

    const vestirLado = (col, op, celula) => {
        if (!celula || !selecao) return celula;
        const lado = LADO_DO_GRUPO[col.group];
        if (!lado) return celula;
        const lados = selecao.lados(op) ?? {};
        const info = lados[lado];
        if (!info?.tem) return celula;
        // No par internacional a volta não se marca sozinha — no lugar do checkbox ela mostra o
        // badge. Mas o clique nela vale como na ida: marca o par pela ida, e a volta acende junto
        // (`selecaoDeImportacao`). Sem isto, só metade da linha respondia.
        const alvo = info.par ? 'ida' : lado;
        const clicavel = info.par ? !!lados.ida?.pode : info.pode;
        const pontas = pontasDoLado[lado];
        const classes = [
            celula.props.className,
            info.marcado ? 'malha-sel' : null,
            info.marcado && pontas?.ini === col.key ? 'malha-sel-ini' : null,
            info.marcado && pontas?.fim === col.key ? 'malha-sel-fim' : null,
            clicavel ? 'malha-lado-click' : null,
        ].filter(Boolean).join(' ');
        return React.cloneElement(celula, {
            className: classes,
            onClick: clicavel ? (e) => {
                // Clique num campo, botão ou no label do checkbox é DELE: com a planilha
                // editável a mesma célula é um textarea, e marcar a importação ao começar a
                // digitar seria efeito colateral. O label já alterna o checkbox sozinho.
                if (e.target.closest('input, textarea, select, button, a, label')) return;
                selecao.onAlternar(op, alvo);
            } : undefined,
        });
    };

    // Linhas marcadas, pelo `_id`: o Excluir tira, e o clique direito destaca, copia e cola nelas. Toda
    // linha se marca; a que não sai (`motivoNaoExcluir`) fica na malha no Excluir.
    const excluivel = !!onExcluir;
    const [paraExcluir, setParaExcluir] = useState(() => new Set());
    const motivoDaLinha = (op) => (excluivel && motivoNaoExcluir ? motivoNaoExcluir(op) : null);
    const idsMarcaveis = excluivel ? opcoes.map(op => String(op._id)) : [];
    const saemMarcadas = opcoes.filter(op => paraExcluir.has(String(op._id)) && !motivoDaLinha(op));
    const todasParaExcluir = idsMarcaveis.length > 0 && idsMarcaveis.every(id => paraExcluir.has(id));
    // A marca sai com a linha: a que deixou a tela (excluída, ou relida do banco com outro `_id`) ou
    // deixou de poder sair não fica contando no botão.
    const chaveMarcaveis = idsMarcaveis.join(',');
    useEffect(() => {
        setParaExcluir(prev => {
            if (!prev.size) return prev;
            const vivas = new Set(chaveMarcaveis ? chaveMarcaveis.split(',') : []);
            const ficam = [...prev].filter(id => vivas.has(id));
            return ficam.length === prev.size ? prev : new Set(ficam);
        });
    }, [chaveMarcaveis]);
    // Um trecho de linhas de uma vez, na ordem da tabela (`ordemNaTabela`): Shift + clique marca da
    // última linha clicada até esta; segurar e arrastar pela rota marca as linhas por onde o mouse
    // passa — ou desmarca, quando começou numa marcada. O clique simples continua alternando uma.
    const ancoraRef = useRef(null);
    const arrastoRef = useRef(null);
    const linhasEntre = (a, b) => {
        const pa = ordemNaTabela.indexOf(a);
        const pb = ordemNaTabela.indexOf(b);
        return pa < 0 || pb < 0 ? [b] : ordemNaTabela.slice(Math.min(pa, pb), Math.max(pa, pb) + 1);
    };
    const comTrecho = (base, indices, marcar) => {
        const nova = new Set(base);
        indices.forEach(i => {
            const id = String(opcoes[i]?._id);
            if (marcar) nova.add(id); else nova.delete(id);
        });
        return nova;
    };
    const pressionarRota = (e, op, index) => {
        if (e.button !== 0) return;
        // Sem isto, arrastar seleciona o texto das rotas em vez de marcar as linhas.
        e.preventDefault();
        const ancora = ancoraRef.current !== null ? opcoes.findIndex(o => String(o._id) === ancoraRef.current) : -1;
        if (e.shiftKey && ancora >= 0) {
            setParaExcluir(prev => comTrecho(prev, linhasEntre(ancora, index), true));
            return;
        }
        const marcar = !paraExcluir.has(String(op._id));
        arrastoRef.current = { inicio: index, marcar, base: new Set(paraExcluir) };
        ancoraRef.current = String(op._id);
        setParaExcluir(prev => comTrecho(prev, [index], marcar));
    };
    const passarPelaRota = (index) => {
        const a = arrastoRef.current;
        if (a) setParaExcluir(comTrecho(a.base, linhasEntre(a.inicio, index), a.marcar));
    };
    useEffect(() => {
        const soltar = () => { arrastoRef.current = null; };
        document.addEventListener('mouseup', soltar);
        return () => document.removeEventListener('mouseup', soltar);
    }, []);
    const alternarTodasParaExcluir = () => setParaExcluir(todasParaExcluir ? new Set() : new Set(idsMarcaveis));
    // Tira as que podem sair e avisa das que estão no evento, que ficam.
    const excluirMarcadas = () => {
        const ficam = paraExcluir.size - saemMarcadas.length;
        if (saemMarcadas.length) onExcluir(saemMarcadas.map(op => String(op._id)));
        if (ficam > 0) {
            toast.warning((ficam === 1
                ? `${saemMarcadas.length ? '1 linha marcada ficou' : 'A linha marcada não saiu'}: está nas opções do evento.`
                : `${saemMarcadas.length ? `${ficam} linhas marcadas ficaram` : `As ${ficam} linhas marcadas não saíram`}: estão nas opções do evento.`)
                + ' O RSVP as retira na Importação das Opções de Aéreo antes.');
        }
        setParaExcluir(new Set());
    };

    // ── Clique direito: destacar, copiar e colar (só quem edita) ─────────────
    const comMenu = editavel && !!onAlterarOpcoes;
    const [menu, setMenu] = useState(null);
    // O que o Colar cola. Ao abrir o menu, o clipboard é lido (`lerParaColar`): o texto de fora com o
    // formato de cópia de malha passa a ser a cópia, e o sem formato deixa o Colar avisando que é
    // inválido, em vez de colar calado a cópia velha.
    const [copiado, setCopiado] = useState(() => lerCopiaDaMalha());
    const [clipboardInvalido, setClipboardInvalido] = useState(false);
    const aColar = copiado;
    // O item do menu sob o mouse (`MenuContextoMalha` → onPrever): copiar pinta de azul o que vai
    // ser copiado, e colar pinta de âmbar o que vai ser sobrescrito.
    const [previa, setPrevia] = useState(null);
    // O colar em opção que está no evento, esperando a confirmação: { alvosLinha, itens, noEvento }.
    const [colagemPendente, setColagemPendente] = useState(null);
    const fecharMenu = () => {
        setMenu(null);
        setPrevia(null);
    };
    // Esc solta o que foi copiado, como numa planilha: some o tracejado, e o Colar fica com o que estiver
    // no clipboard.
    useEffect(() => {
        if (!copiado || menu || colagemPendente) return undefined;
        const aoTeclar = (e) => {
            if (e.key !== 'Escape') return;
            guardarCopiaDaMalha(null);
            setCopiado(null);
        };
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [copiado, menu, colagemPendente]);
    // O destaque que vale na célula: o da linha por baixo, e o da própria célula por cima.
    const destaqueDe = (op, campo) => ({
        ...(op?.destaques?.[LINHA] || {}),
        ...(campo !== LINHA ? (op?.destaques?.[campo] || {}) : {}),
    });
    // As cores que a malha já usa, para o Destacar oferecer de novo (as fora da paleta).
    const coresUsadas = useMemo(() => {
        const usadas = { texto: [], fundo: [] };
        opcoes.forEach(op => Object.values(op.destaques || {}).forEach(d => ['texto', 'fundo'].forEach(tipo => {
            if (d?.[tipo] && !usadas[tipo].includes(d[tipo])) usadas[tipo].push(d[tipo]);
        })));
        return usadas;
    }, [opcoes]);
    // A posição de cada opção na tabela: copiar e colar várias linhas seguem a ordem que se vê.
    const posicaoNaTabela = useMemo(() => {
        const pos = new Map();
        gruposTabela.forEach(g => g.rows.forEach(r => pos.set(r.i, pos.size)));
        return pos;
    }, [gruposTabela]);
    const ordemNaTabela = useMemo(() => gruposTabela.flatMap(g => g.rows.map(r => r.i)), [gruposTabela]);

    const abrirMenu = (e, col, op, index) => {
        // Shift + clique direito fica com o navegador (copiar o texto da célula, inspecionar).
        if (!comMenu || e.shiftKey || col.selecao) return;
        e.preventDefault();
        // O menu alcança as linhas marcadas quando a clicada é uma delas; senão, só ela.
        const naSelecao = paraExcluir.has(String(op._id));
        const alvosLinha = (naSelecao
            ? opcoes.map((o, i) => (paraExcluir.has(String(o._id)) ? i : null)).filter(i => i !== null)
            : [index]).sort((a, b) => (posicaoNaTabela.get(a) ?? a) - (posicaoNaTabela.get(b) ?? b));
        // O destaque vai na mesma célula de cada linha marcada (na 1ª coluna, a linha inteira). Sem
        // marcação, a célula clicada; a mesclada leva as linhas dela no `destacar` (`comAMescla`).
        const alvosDestaque = naSelecao ? alvosLinha : [index];
        setMenu({ x: e.clientX, y: e.clientY, campo: col.control ? LINHA : (col.field || col.key), alvosDestaque, alvosLinha, clicada: index });
        // O Colar cola o que está no clipboard, como numa planilha: lido aqui, ao abrir o menu. Sem
        // acesso (o navegador não deixou), vale a cópia da própria malha.
        setClipboardInvalido(false);
        lerParaColar().then(({ copia, invalido }) => {
            setCopiado(copia);
            setClipboardInvalido(invalido);
        });
    };

    // Nas colunas mescladas, o destaque é da célula mesclada: vai para todas as linhas que ela cobre
    // na mescla do grupo — sem a separação das linhas marcadas, que some ao desmarcar. Assim a
    // célula nunca mostra um destaque que só uma das linhas dela tem, e o Modelo Cliente, que pinta
    // a primeira, sai igual. O TKT é um valor do grupo inteiro e mescla o grupo todo.
    const comAMescla = (campo, indices) => {
        if (!MERGE_KEYS.includes(campo)) return indices;
        const todos = new Set(indices);
        gruposTabela.forEach(g => {
            const posicoes = g.rows.map(r => r.i);
            if (!posicoes.some(i => todos.has(i))) return;
            if (campo === 'tktPessoa' || campo === 'tktGrupo') {
                posicoes.forEach(i => todos.add(i));
                return;
            }
            mesclarColuna(g.rows.map(({ op }) => valorMerge(op, campo)), extrasDaMescla(g.key, campo, trechos)).forEach(run => {
                const daCelula = run ? run.cobre.map(c => posicoes[c]) : [];
                if (daCelula.some(i => todos.has(i))) daCelula.forEach(i => todos.add(i));
            });
        });
        return [...todos];
    };

    const destacar = (tipo, cor) => {
        const { campo } = menu;
        // A linha inteira leva junto as células mescladas que a cobrem (Origem, TKT, Num. Part., Obs.),
        // pela mesma regra do destaque de uma célula mesclada (`comAMescla`). A mesclada mostra a
        // primeira linha dela: sem isso, o destaque de outra linha do grupo só aparecia na parte que a
        // marcação separa, e sumia ao desmarcar. No Modelo Cliente, que pinta a primeira, sai igual.
        const porCampo = campo === LINHA
            ? [[LINHA, menu.alvosDestaque], ...MERGE_KEYS.map(k => [k, comAMescla(k, menu.alvosDestaque)])]
            : [[campo, comAMescla(campo, menu.alvosDestaque)]];
        const destaquesDe = new Map();
        porCampo.forEach(([c, indices]) => indices.forEach(i => {
            const destaques = destaquesDe.get(i) ?? { ...(opcoes[i].destaques || {}) };
            const daCelula = { ...(destaques[c] || {}) };
            if (cor) daCelula[tipo] = cor; else delete daCelula[tipo];
            if (Object.keys(daCelula).length) destaques[c] = daCelula; else delete destaques[c];
            destaquesDe.set(i, destaques);
        }));
        onAlterarOpcoes([...destaquesDe].map(([i, destaques]) => ({ i, campos: { destaques: Object.keys(destaques).length ? destaques : null } })));
    };

    const rotuloCopia = (c) => `${ladosDaCopia(c).length === 1 ? 'Um lado de ' : ''}${c.itens.length} ${c.itens.length !== 1 ? 'linhas' : 'linha'}`;
    // A linha como a tela a mostra, para o texto do clipboard: com o total e o TKT do grupo dela.
    const linhaComoNaTela = (i) => {
        const op = opcoes[i];
        const grupo = gruposTabela.find(g => g.rows.some(r => r.i === i));
        const doGrupo = grupo ? grupo.rows.map(r => r.op) : [op];
        const pax = grupo ? paxDoBloco(grupo) : op.numParticipantes;
        return {
            ...op,
            total: calcularTotal(op),
            tktPessoa: tktPorPessoaStr(doGrupo),
            tktGrupo: tktPorGrupoStr(doGrupo, pax),
            numParticipantes: String(op.numParticipantes ?? '') || String(pax ?? ''),
        };
    };
    const camposDoLado = (l) => (l === 'ida' ? IDA_KEYS : RET_KEYS);
    // Copia os lados escolhidos das linhas, como numa planilha: a cópia leva só os campos deles.
    const copiar = (lados) => {
        const campos = lados.flatMap(camposDoLado);
        const emBranco = Object.fromEntries(['ida', 'ret'].filter(l => !lados.includes(l)).flatMap(camposDoLado).map(k => [k, '']));
        const copia = {
            itens: menu.alvosLinha.map(i => Object.fromEntries(campos.map(k => [k, opcoes[i][k] ?? '']))),
            // As linhas de onde saiu, para o tracejado: em outra malha nenhuma casa, e ele não aparece.
            ids: menu.alvosLinha.map(i => String(opcoes[i]._id)),
            // As mesmas linhas como texto de planilha, nas colunas da visão de informações completas
            // (`textoDasLinhas`), com sigla, cidade e aeroporto de cada ponta e o lado não copiado em
            // branco: vai ao clipboard, para colar no Excel. Colado aqui de volta, o Colar reconhece o texto.
            texto: textoDasLinhas(menu.alvosLinha.map(i => ({ ...linhaComoNaTela(i), ...emBranco }))),
        };
        guardarCopiaDaMalha(copia);
        setCopiado(copia);
        setClipboardInvalido(false);
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(copia.texto).catch(() => {});
        fecharMenu();
        toast.success(`Copiado: ${rotuloCopia(copia)}, também como texto para colar numa planilha. Esc solta a cópia.`);
    };
    // Copiar um lado que nenhuma das linhas tem levaria uma cópia vazia.
    const motivoNaoCopiar = (lados) => {
        if (!menu) return null;
        const tem = (l) => menu.alvosLinha.some(i => camposDoLado(l).some(k => String(opcoes[i]?.[k] ?? '').trim() !== ''));
        if (lados.some(tem)) return null;
        const nome = lados.length > 1 ? 'voo' : (lados[0] === 'ida' ? 'ida' : 'volta');
        const n = menu.alvosLinha.length;
        return n === 1 ? `A linha não tem ${nome}.` : `Nenhuma das ${n} linhas tem ${nome}.`;
    };
    // Onde o colar cai, como numa planilha: uma linha copiada vai em todas as do alvo (as marcadas, ou
    // a clicada); várias vão uma em cada — nas marcadas quando são as mesmas tantas, e senão a partir
    // da clicada, descendo na ordem da tabela. A prévia do Colar pinta essas linhas.
    const destinoDaColagem = (() => {
        if (!menu || !aColar) return null;
        const k = aColar.itens.length;
        if (k === 1 || menu.alvosLinha.length === k) return menu.alvosLinha;
        const p = ordemNaTabela.indexOf(menu.clicada);
        return p < 0 ? [] : ordemNaTabela.slice(p, p + k);
    })();
    // Os lados que a cópia traz, pelo conteúdo (`ladosDaCopia`): um lado só vai como Ida ou como Volta.
    const motivoDosLados = (lados, verbo) => {
        const vieram = ladosDaCopia(aColar);
        if (!vieram.length) return 'A cópia não tem voo.';
        return lados.length > 1 && vieram.length === 1 ? `Veio um lado só: ${verbo} como Ida ou como Volta.` : null;
    };
    // Por que o Colar daqueles lados não vale; `lados` null pergunta só se há o que colar.
    const motivoNaoColar = (lados) => {
        if (!aColar) return 'Nada para colar: copie linhas da malha ou de uma planilha.';
        const k = aColar.itens.length;
        if (destinoDaColagem && destinoDaColagem.length < k) {
            return `Foram copiadas ${k} linhas, e desta para baixo só há ${destinoDaColagem.length}. `
                + 'Clique numa linha mais acima, ou use o Duplicar, que cria linhas novas.';
        }
        return lados ? motivoDosLados(lados, 'cole') : null;
    };
    // O Duplicar não cai em linha nenhuma da tabela: vale só o que a cópia traz.
    const motivoNaoDuplicar = (lados) => {
        if (!aColar) return 'Nada para duplicar: copie linhas da malha ou de uma planilha.';
        return lados ? motivoDosLados(lados, 'duplique') : null;
    };
    // Colar em opção que está no evento pede confirmação (`colagemPendente`), com a prévia na tabela:
    // a colagem muda a linha da malha, e a opção do evento fica como estava (a Importação mostra a
    // diferença ao RSVP).
    const aplicarColagem = ({ alvosLinha, itens }) => {
        onAlterarOpcoes(alvosLinha.map((i, j) => ({ i, campos: { ...(itens.length === 1 ? itens[0] : itens[j]) } })));
        const n = alvosLinha.length;
        toast.success(`Colado em ${n} ${n !== 1 ? 'linhas' : 'linha'} — clique em Salvar para gravar`);
    };
    // Cola dos itens só os campos dos lados escolhidos (`camposDoColar`). Sem lados, o clipboard tinha
    // texto que não é cópia de malha (`clipboardInvalido`): avisa.
    const colar = (lados) => {
        if (!lados) {
            fecharMenu();
            toast.error(AVISO_CLIPBOARD_INVALIDO);
            return;
        }
        const pedido = { alvosLinha: destinoDaColagem, itens: aColar.itens.map(item => camposDoColar(item, lados, aColar)) };
        const noEvento = pedido.alvosLinha.filter(i => opcoes[i]?.id && importadas && importadas.has(String(opcoes[i].id))).length;
        fecharMenu();
        if (noEvento) setColagemPendente({ ...pedido, noEvento, lados });
        else aplicarColagem(pedido);
    };
    // O Duplicar: a cópia, nos lados escolhidos, vira linhas novas em vez de cair nas da tabela — as que a Nova linha
    // montaria com o Colar dela. Quem relaciona e põe na malha é a página.
    const duplicar = (lados) => {
        fecharMenu();
        if (!lados) {
            toast.error(AVISO_CLIPBOARD_INVALIDO);
            return;
        }
        onDuplicar(aColar.itens.map(item => {
            const nova = { ...createEmptyOpcao(), ...camposDoColar(item, lados, aColar) };
            return { ...nova, total: calcularTotal(nova) };
        }));
    };

    // O que o copiar e o colar alcançam, pintado na tabela: com o menu aberto, as linhas do alvo num
    // azul leve; com o mouse num Copiar, as células dos lados dele em azul; num Colar, as dos lados dele
    // em âmbar, já com o que vai entrar; e o que está copiado, tracejado nos lados copiados, até o Esc. A
    // célula mesclada pinta se uma das linhas dela entra.
    const ladoDaColuna = (col) => {
        const campo = col.field || col.key;
        if (IDA_KEYS.includes(campo)) return 'ida';
        return RET_KEYS.includes(campo) ? 'ret' : null;
    };
    // Com o modal de confirmação aberto, a prévia do colar continua na tabela.
    const alvoAberto = menu ? menu.alvosLinha : (colagemPendente ? colagemPendente.alvosLinha : null);
    const previaAtiva = colagemPendente ? { tipo: 'colar', lados: colagemPendente.lados } : previa;
    const paraIds = (indices) => (indices ? new Set(indices.map(i => String(opcoes[i]?._id))) : null);
    const idsDoAlvo = paraIds(alvoAberto);
    // A prévia do copiar fica nas linhas do alvo; a do colar, nas do destino, que pode descer além delas.
    const idsDaPrevia = paraIds(previaAtiva?.tipo === 'colar' ? (colagemPendente ? colagemPendente.alvosLinha : destinoDaColagem) : alvoAberto);
    // O tracejado é da cópia da malha, nos lados copiados: com outra coisa no clipboard, a cópia sai e
    // ele some, como numa planilha (`lerParaColar`). O texto de fora não tem linhas de onde saiu.
    const idsCopiados = copiado?.ids ? new Set(copiado.ids) : null;
    const ladosCopiados = copiado ? ladosDaCopia(copiado) : [];
    const ladosDaPrevia = previaAtiva?.lados || [];
    // Na prévia do colar, cada linha do destino mostra o que vai receber: { `_id`: { campo: valor } }.
    const itensDaPrevia = (() => {
        if (previaAtiva?.tipo !== 'colar' || !aColar) return null;
        if (colagemPendente) {
            return new Map(colagemPendente.alvosLinha.map((i, j) => [String(opcoes[i]?._id),
                colagemPendente.itens.length === 1 ? colagemPendente.itens[0] : colagemPendente.itens[j]]));
        }
        if (!destinoDaColagem) return null;
        return new Map(destinoDaColagem.map((i, j) => [String(opcoes[i]?._id),
            camposDoColar(aColar.itens.length === 1 ? aColar.itens[0] : aColar.itens[j], ladosDaPrevia, aColar)]));
    })();
    const marcarCopia = (col, celula, cobertas) => {
        if (!celula || (!idsDoAlvo && !idsCopiados)) return celula;
        const lado = ladoDaColuna(col);
        const entra = (ids) => ids && cobertas.some(o => ids.has(String(o._id)));
        const classes = [celula.props.className];
        // O Copiar e o Colar levam os lados que eles escolhem.
        const naPrevia = previaAtiva && lado && ladosDaPrevia.includes(lado);
        if (naPrevia && entra(idsDaPrevia)) {
            classes.push(`malha-previa-${previaAtiva.tipo}`);
        } else if (entra(idsDoAlvo)) {
            classes.push('malha-alvo');
        }
        if (lado && ladosCopiados.includes(lado) && entra(idsCopiados)) classes.push('malha-copiado');
        return classes.length > 1 ? React.cloneElement(celula, { className: classes.filter(Boolean).join(' ') }) : celula;
    };

    // A linha marcada para excluir veste o véu inteira, da primeira à última coluna visível. A célula
    // mesclada cobre várias linhas e só veste quando todas as que ela cobre estão marcadas: vestida com
    // uma só, diria que a vizinha também sai.
    const pontaIni = visibleCols[0]?.key;
    const pontaFim = visibleCols[visibleCols.length - 1]?.key;
    const vestirLinha = (col, celula, cobertas) => {
        if (!celula || !paraExcluir.size || !cobertas.every(o => paraExcluir.has(String(o._id)))) return celula;
        const classes = [
            celula.props.className,
            'malha-sel',
            col.key === pontaIni ? 'malha-sel-ini' : null,
            col.key === pontaFim ? 'malha-sel-fim' : null,
        ].filter(Boolean).join(' ');
        return React.cloneElement(celula, { className: classes });
    };

    // Renderiza uma célula da tabela (controle, passageiros, ou campo editável).
    // Para colunas mescladas, `mergeCell` traz { span, value, targets } da run (ou null =
    // coberta pelo rowspan anterior); `grupoRows` mapeia targets do grupo p/ índices reais.
    const renderCelula = (col, op, index, mergeCell, grupoRows) => {
        const ehMerge = MERGE_KEYS.includes(col.key);
        if (ehMerge && mergeCell === null) return null; // coberta pelo rowspan anterior
        const mergeProps = ehMerge ? { rowSpan: mergeCell.span } : {};

        // Cor de verificação: só nas colunas verificáveis e com o painel aberto.
        const vstatus = (trechos.length > 0 && coresAtivas)
            ? verificacaoCelulas[index]?.[COL_VERIF[col.key]] : null;
        const corCelula = vstatus ? CELL_TINT[vstatus] : undefined;
        // O destaque do Aéreo (clique direito). Com o painel de conferência aberto, a cor dele vence o
        // fundo: é a conferência que se está lendo naquela hora.
        const dest = destaqueDe(op, col.control ? LINHA : (col.field || col.key));
        // O que vai no campo de dentro da célula, que não herda a cor nem a fonte do td.
        const fonteDoCampo = {
            ...(dest.texto ? { color: dest.texto } : {}),
            ...(dest.negrito ? { fontWeight: 700 } : {}),
            ...(dest.italico ? { fontStyle: 'italic' } : {}),
            ...(dest.sublinhado ? { textDecoration: 'underline' } : {}),
        };
        const pintura = {
            ...(dest.fundo && !corCelula ? { backgroundColor: dest.fundo } : {}),
            ...fonteDoCampo,
        };

        if (col.control) {
            // A rota é onde se marca a linha; marcada, ela fica em negrito.
            const motivo = motivoDaLinha(op);
            const marcada = paraExcluir.has(String(op._id));
            return (
                <td key={col.key} {...mergeProps}
                    className={'text-center align-middle text-nowrap' + (marcada || dest.negrito ? ' fw-bold' : ' fw-semibold') + (excluivel ? ' malha-lado-click' : '')}
                    style={{ color: BRAND_NAVY, fontSize: 13, lineHeight: 1.35, ...pintura }}
                    title={excluivel
                        ? (marcada ? 'Linha marcada: clique para desmarcar' : 'Clique para marcar a linha')
                            + '. Shift + clique ou arrastar marca várias' + (motivo ? `. ${motivo}` : '')
                        : undefined}
                    onMouseDown={excluivel ? (e) => pressionarRota(e, op, index) : undefined}
                    onMouseEnter={excluivel ? () => passarPelaRota(index) : undefined}>
                    {rotasControl(op).map((linha, i) => <div key={i}>{linha}</div>)}
                </td>
            );
        }
        if (col.selecao) {
            const info = (selecao.lados(op) ?? {})[col.lado];
            // O padding vai no invólucro: o `td` da planilha zera o próprio com !important.
            // Sem NADA a mostrar — a malha não tem este lado, ou ele não se marca e não tem badge
            // que explique — a célula fica com o traço, que é como a planilha diz "aqui não há".
            const vazia = !info?.tem || (!info.pode && !info.rotulo);
            return (
                <td key={col.key} className="align-middle">
                    {/* Em COLUNA: o badge conta o estado daquele lado e cresce com o texto
                        ("Alterada no evento"), e ao lado do checkbox ele empurrava a coluna ou
                        espremia o rótulo. Embaixo, cada um tem a linha dele. */}
                    <div className="fin-pp d-flex flex-column align-items-center justify-content-center gap-1"
                        style={{ padding: '4px 8px' }}>
                        {vazia ? (
                            <span className="fin-pp__cell-muted" style={{ fontSize: 12 }}>—</span>
                        ) : (
                            <>
                                {/* Não dando para marcar, o checkbox some em vez de ficar preso:
                                    desabilitado ele só convida a um clique que não faz nada, e
                                    quem diz o porquê é o badge logo abaixo. */}
                                {info.pode && (
                                    <label className="d-flex align-items-center gap-1 mb-0" title={info.titulo || undefined}
                                        style={{ fontSize: FONTE_IMPORTAR.tamanho, fontWeight: FONTE_IMPORTAR.peso,
                                            color: BRAND_NAVY, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                                        <input type="checkbox" className="fin-pp__check" checked={!!info.marcado}
                                            onChange={() => selecao.onAlternar(op, col.lado)} />
                                        Importar
                                    </label>
                                )}
                                {info.rotulo && (
                                    <span className={'fin-pp__pill ' + (info.cor || 'is-muted') + (info.icone ? ' fin-pp__pill--plain' : '')}
                                        style={{ fontSize: FONTE_BADGE_LADO.tamanho, fontWeight: FONTE_BADGE_LADO.peso, whiteSpace: 'nowrap' }}
                                        title={info.titulo || undefined}>
                                        {/* O ícone entra no lugar do ponto do pill: o globo do internacional. */}
                                        {info.icone === 'globo' && <FaGlobeAmericas size={10} />}
                                        {info.rotulo}
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                </td>
            );
        }
        if (col.key === 'numParticipantes') {
            // Mesclada como as outras (`onEditarMesclado`, com o modal como mais uma linha), no
            // campo numérico: só dígitos.
            if (editavel && onEditarMesclado) {
                const alvosPax = mergeCell.targets.map(k => grupoRows[k].i);
                return (
                    <td key={col.key} {...mergeProps} className="p-0 align-middle" title="Passageiros" style={pintura}>
                        <input type="text" inputMode="numeric"
                            className="form-control form-control-sm border-0 bg-transparent shadow-none w-100 text-center"
                            style={fonteDoCampo}
                            value={mergeCell.value}
                            onChange={e => onEditarMesclado(alvosPax, 'numParticipantes', String(e.target.value).replace(/\D/g, ''))} />
                    </td>
                );
            }
            return (
                <td key={col.key} {...mergeProps} className="text-center align-middle" style={{ color: BRAND_NAVY, fontSize: 13, ...pintura }} title="Passageiros">
                    {mergeCell.value}
                </td>
            );
        }
        if (col.key === 'opcao') {
            // Só o número. O "No evento" que ficava aqui embaixo saiu: a coluna Importar diz o
            // mesmo e diz melhor, POR LADO — uma opção pode estar no evento só na ida.
            return (
                <td key={col.key} className={'text-center align-middle ' + (dest.negrito ? 'fw-bold' : 'fw-semibold')} style={{ color: BRAND_NAVY, fontSize: 13, ...pintura }} title="Posição da opção no trecho">
                    {String(op.opcao ?? '')}
                </td>
            );
        }
        // Colunas calculadas (read-only): total por opção; TKT pessoa/grupo do grupo (mesclado).
        // Na prévia do colar, o que a linha vai receber no lugar do que ela tem (só os campos copiados).
        const colado = itensDaPrevia ? itensDaPrevia.get(String(op._id)) : null;
        // Tarifa + taxas do lado: a soma, só leitura. Para editar em separado, a visualização completa.
        if (col.soma) {
            return (
                <td key={col.key} className="text-center align-middle" style={{ color: BRAND_NAVY, fontSize: 13, ...pintura }}
                    title="Tarifa & taxas somadas. Para ver e editar em separado, abra a visualização completa (o olho).">
                    {tarifaComTaxas(colado ? { ...op, ...colado } : op, col.soma)}
                </td>
            );
        }
        if (COLS_CALCULADAS.includes(col.key)) {
            const conteudo = col.key === 'total' ? calcularTotal(colado ? { ...op, ...colado } : op) : mergeCell.value;
            return (
                <td key={col.key} {...mergeProps} className="text-center align-middle" style={{ color: BRAND_NAVY, fontSize: 13, ...pintura }} title="Calculado automaticamente">
                    {conteudo}
                </td>
            );
        }
        const field = col.field || col.key;
        const valGravado = colado && field in colado ? String(colado[field] ?? '')
            : (ehMerge ? mergeCell.value : String(op[field] ?? ''));
        // A Origem do modo cliente mostra só a cidade de onde sai a primeira perna, centrada na célula, como o
        // Modelo Cliente; com o foco, mostra a conexão inteira, e a edição é a do campo todo.
        const ehOrigemCliente = col.key === 'idaOrigemCidade' && !!col.compactOnly;
        const soPrimeira = ehOrigemCliente && origemEditando !== index;
        const val = soPrimeira ? valGravado.split('\n')[0] : valGravado;
        const linhas = Math.max(1, val.split('\n').length);
        const ehObs = col.key === 'obs';
        // É o MESMO textarea para todas as colunas — só 3 props variam na OBS: alinha à
        // esquerda (como o export) e embrulha SEMPRE na largura máxima (o .malha-grow tem
        // largura fixa = teto), independente da largura da COLUNA — coluna e textarea têm
        // larguras separadas; a célula clipa o excesso horizontal e a altura mostra o
        // texto inteiro quebrado no teto.
        const alvos = ehMerge ? mergeCell.targets.map(k => grupoRows[k].i) : [index];
        const editar = ehMerge ? onEditarMesclado : onEditar;
        // A cidade na frente do aeroporto, uma por linha (a conexão empilha as pernas). Fica fora do
        // campo: quem edita mexe só no aeroporto, e a cidade acompanha a do mesmo lado.
        const campoCidade = CIDADE_DO_AEROPORTO[col.key];
        const cidades = campoCidade ? cidadesDoAeroporto((colado && campoCidade in colado ? colado : op)[campoCidade], val) : null;
        const campo = (
            <textarea rows={ehObs ? 1 : linhas} wrap={ehObs ? 'soft' : 'off'}
                className={`${CLASSE_DO_CAMPO} w-100`}
                style={{
                    resize: 'none', overflow: 'hidden',
                    whiteSpace: ehObs ? 'pre-wrap' : 'nowrap',
                    textAlign: ehObs ? 'left' : 'center',
                    ...fonteDoCampo,
                }}
                value={val}
                disabled={!editavel || !editar}
                onFocus={ehOrigemCliente ? () => setOrigemEditando(index) : undefined}
                onBlur={ehOrigemCliente ? () => setOrigemEditando(null) : undefined}
                onChange={e => {
                    editar(alvos, field, e.target.value);
                    soltarManualSeEstourar(col, e.target.value, e.target);
                }} />
        );
        return (
            <td key={col.key} {...mergeProps} className="p-0 align-middle"
                // `backgroundColor`, e não o atalho `background`: o atalho zera o
                // `background-image`, que é por onde o véu azul do lado marcado entra.
                style={corCelula || dest.fundo ? { backgroundColor: corCelula || dest.fundo } : undefined}
                title={vstatus ? VERIF_TITULO[vstatus] : undefined}>
                {ehObs
                    ? <div className="malha-clip"><div className="malha-grow" data-val={val}>{campo}</div></div>
                    : cidades ? (
                        // "Natal (São Gonçalo do Amarante)": o aeroporto, que é o campo, centrado como nas outras
                        // células, e a cidade com o "(" e o ")" colados nas pontas de cada linha. O campo ocupa a
                        // célula toda, e o clique na cidade cai nele, na linha clicada. Por baixo, a medida repete o
                        // texto do campo, escondida, e só a cidade e o ")" dela aparecem. O destaque vale também na
                        // cidade, como no Modelo Cliente, e na medida, que precisa do negrito e do itálico para
                        // medir o mesmo texto.
                        <div className="malha-clip">
                            <div className="malha-aeroporto-campo">
                                <div aria-hidden="true" className={`${CLASSE_DO_CAMPO} malha-aeroporto-medida`} style={fonteDoCampo}>
                                    {val.split('\n').map((l, i) => (
                                        <React.Fragment key={i}>
                                            {i > 0 && '\n'}
                                            {cidades[i] && <span className="malha-aeroporto-cidade">{cidades[i]} (</span>}
                                            {l}
                                            {cidades[i] && <span className="malha-aeroporto-fecha">)</span>}
                                        </React.Fragment>
                                    ))}
                                </div>
                                {campo}
                            </div>
                        </div>
                    ) : campo}
            </td>
        );
    };

    const alturaRolagem = alturaMaxima || alturaTabela;
    // No encaixe exato: o afastar não tem para onde ir e o % aparece marcado.
    const noEncaixe = zoom <= zoomMinimo + 0.001;

    return (
        <>
        <div className="bg-white mb-4 overflow-hidden" style={{ border: `1px solid ${BRAND_BORDER}`, borderRadius: 18 }}>
            <div className="px-4 py-3" style={{ background: 'linear-gradient(180deg, #fff 0%, #f7fbff 100%)', borderBottom: `1px solid ${BRAND_BORDER}` }}>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <span className="fw-bold" style={{ color: BRAND_NAVY, fontSize: 18 }}>{titulo}</span>
                    <span className="fw-bold text-white rounded-pill" style={{ background: BRAND_BLUE, fontSize: 12, padding: '2px 10px' }}>
                        {opcoes.length}
                    </span>
                    <div className="d-flex align-items-center gap-3 flex-wrap ms-auto">
                        {acoes}
                    </div>
                </div>
                {/* Linha de baixo: a legenda do toggle da 1ª coluna (sem ela o ícone não se explica) à
                    esquerda, e o zoom à direita. Sem espaço, o zoom quebra e continua à direita. */}
                <div className="d-flex align-items-center flex-wrap gap-3 mt-2">
                    <div className="d-flex align-items-center flex-wrap" style={{ color: '#7b8ba7', fontSize: 12, gap: 5 }}>
                        Clique em
                        <FaEye size={11} style={{ color: BRAND_NAVY }} />
                        para mudar a visualização da planilha entre o padrão de exportação do cliente ou informações completas.
                    </div>
                    <div className="d-inline-flex align-items-center gap-2 ms-auto" style={{ flexShrink: 0 }}>
                    {/* Aparece com linha marcada, à esquerda do zoom. Tira da tela; grava no Salvar. A que
                        está no evento fica, com o aviso (`excluirMarcadas`). */}
                    {paraExcluir.size > 0 && (
                        <button type="button" onClick={excluirMarcadas}
                            title="Tira da malha as linhas marcadas. Só na tela: grava no Salvar."
                            className="btn btn-sm d-inline-flex align-items-center gap-1 fw-semibold"
                            style={{ ...ZOOM_BOTAO, border: '1px solid #f0c2c4', color: '#c1232b', fontSize: 12 }}>
                            <FaTrash size={11} />
                            Excluir ({paraExcluir.size})
                        </button>
                    )}
                    <span className="d-inline-flex align-items-center gap-1" style={{ color: BRAND_NAVY, fontSize: 12, flexShrink: 0 }}>
                        <button type="button" disabled={noEncaixe}
                            onClick={() => clicarZoom(-1)} onPointerDown={() => segurarZoom(-1)}
                            onPointerUp={pararZoom} onPointerCancel={pararZoom}
                            title={noEncaixe
                                ? `A planilha já cabe inteira na largura da tela (${Math.round(zoomMinimo * 100)}%)`
                                : 'Afastar a planilha — segure para ir aos poucos'}
                            aria-label="Afastar a planilha"
                            className="btn btn-sm d-inline-flex align-items-center"
                            style={{ ...ZOOM_BOTAO }}>
                            <FaSearchMinus size={12} />
                        </button>
                        {/* O % é o campo: clicar troca por um input para digitar o valor. */}
                        {digitandoZoom === null ? (
                            <button type="button" onClick={() => setDigitandoZoom(String(Math.round(zoom * 100)))}
                                title={noEncaixe
                                    ? 'Encaixe exato na largura da tela — clique para digitar outro zoom'
                                    : 'Clique para digitar o zoom'}
                                aria-label="Digitar o zoom da planilha" aria-pressed={noEncaixe}
                                className="btn btn-sm p-0 d-inline-flex align-items-center justify-content-center"
                                style={{
                                    minWidth: 40, color: BRAND_NAVY, fontSize: 12, borderRadius: 8, padding: '2px 2px',
                                    background: noEncaixe ? BRAND_SKY : 'transparent',
                                    border: `1px solid ${noEncaixe ? BRAND_BORDER : 'transparent'}`,
                                    fontWeight: noEncaixe ? 600 : 400,
                                }}>
                                {Math.round(zoom * 100)}%
                            </button>
                        ) : (
                            <input type="text" inputMode="numeric" value={digitandoZoom} autoFocus
                                onChange={e => setDigitandoZoom(e.target.value.replace(/\D/g, '').slice(0, 3))}
                                onFocus={e => e.target.select()}
                                onBlur={confirmarZoomDigitado}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') { e.preventDefault(); confirmarZoomDigitado(); }
                                    if (e.key === 'Escape') { e.preventDefault(); setDigitandoZoom(null); }
                                }}
                                aria-label="Zoom da planilha, em %"
                                title={`A partir de ${Math.round(zoomMinimo * 100)}%, que é o encaixe na largura da tela — abaixo disso entra o mínimo`}
                                style={{ width: 40, textAlign: 'center', color: BRAND_NAVY, fontSize: 12, background: '#fff', border: `1px solid ${BRAND_BORDER}`, borderRadius: 8, padding: '2px 2px' }} />
                        )}
                        <button type="button"
                            onClick={() => clicarZoom(1)} onPointerDown={() => segurarZoom(1)}
                            onPointerUp={pararZoom} onPointerCancel={pararZoom}
                            title="Aproximar a planilha — segure para ir aos poucos" aria-label="Aproximar a planilha"
                            className="btn btn-sm d-inline-flex align-items-center"
                            style={{ ...ZOOM_BOTAO }}>
                            <FaSearchPlus size={12} />
                        </button>
                    </span>
                    </div>
                </div>
            </div>

            {/* Horizontal nativo, vertical só por código: com `overflow-y: hidden` o navegador
                para de decidir a rolagem vertical daqui (mas `scrollTop` continua valendo), e a
                cascata abaixo fica dona do gesto inteiro — sem troca de dono no meio dele. Com
                `alturaMaxima` (Portação) nada disso vale: lá é a rolagem nativa de sempre.

                Nos DOIS casos o overflow é inline, e nenhum deles usa a classe `overflow-auto` do
                Bootstrap: ela é `overflow: auto !important`, e o !important ganhava do inline que o
                arraste de coluna põe para esconder a barra. O componente era o mesmo, o estilo não:
                o flick da barra continuava só na Portação. */}
            <div ref={areaTabelaRef}
                style={{
                    ...(alturaRolagem ? { maxHeight: alturaRolagem } : null),
                    overflowX: 'auto',
                    overflowY: alturaMaxima ? 'auto' : 'hidden',
                }}>
                <style>{`
                    .malha-table.table td { padding: 0 !important; }
                    /* Auto-altura da OBS 100% CSS: o ::after replica o texto (data-val)
                       na MESMA métrica do textarea (form-control-sm) e dimensiona a área
                       do grid; o textarea só preenche. Largura FIXA no teto: o textarea
                       embrulha sempre nos ${LARGURA_MAX_COL}px, independente da largura
                       da coluna (larguras separadas; a célula clipa o excesso). */
                    /* Invólucro da célula OBS: largura INTRÍNSECA zero (width: 0) para o
                       .malha-grow fixo em ${LARGURA_MAX_COL}px NÃO travar o encolhimento da
                       coluna (tabela em max-content = layout auto); min-width: 100% ocupa a
                       coluna real e o overflow clipa o excesso. A altura (texto embrulhado
                       no teto) atravessa normalmente. */
                    .malha-clip { width: 0; min-width: 100%; overflow: hidden; }
                    .malha-grow { display: grid; width: ${LARGURA_MAX_COL}px; }
                    .malha-grow::after {
                        content: attr(data-val) ' ';
                        visibility: hidden;
                        white-space: pre-wrap;
                        overflow-wrap: break-word;
                        grid-area: 1 / 1;
                        padding: 0.25rem 0.5rem;
                        font-size: 0.875rem;
                        line-height: 1.5;
                    }
                    .malha-grow > textarea { grid-area: 1 / 1; }
                    /* Aeroporto com a cidade na frente. A medida divide a célula do grid com o campo e tem
                       as classes dele (CLASSE_DO_CAMPO): a mesma fonte, linha, padding e centralização, então
                       cada linha dela cai em cima da mesma linha do campo. O padding de hoje é o de uma regra
                       global de .form-control (10px 12px), e não o do Bootstrap: copiado à mão, ele deixava a
                       cidade 6px acima do aeroporto. A cidade e o ")" têm largura zero, para não tirar o
                       aeroporto do centro, e transbordam para fora dele: a cidade para a esquerda
                       (flex-end), o ")" para a direita. O que passa da coluna o .malha-clip corta. */
                    .malha-aeroporto-campo { display: grid; }
                    .malha-aeroporto-campo > .malha-aeroporto-medida,
                    .malha-aeroporto-campo > textarea { grid-area: 1 / 1; }
                    .malha-aeroporto-medida { visibility: hidden; white-space: pre; text-align: center; }
                    .malha-aeroporto-cidade,
                    .malha-aeroporto-fecha {
                        display: inline-flex;
                        width: 0;
                        visibility: visible;
                        color: #7b8ba7;
                        white-space: pre;
                        user-select: none;
                    }
                    .malha-aeroporto-cidade { justify-content: flex-end; }
                    /* Lado marcado para importar: um véu azul dentro da célula, forte na borda e
                       fraco no meio — o desenho de uma sombra interna, feito com gradiente.

                       Vai como CAMADA DE IMAGEM, e não como cor de fundo, para se somar à cor de
                       verificação em vez de apagá-la: verde-que-casa continua verde por baixo do
                       azul. Por isso o estilo inline da verificação usa backgroundColor, e não o
                       atalho background, que zeraria esta imagem.

                       O SELETOR É LONGO DE PROPÓSITO. A regra de célula do Bootstrap
                       (.table>:not(caption)>*>*, bootstrap.css:19000) já define
                       background-image — é assim que ele pinta o "accent" da tabela — e tem
                       especificidade 0,1,1. Uma classe solta (.malha-sel, 0,1,0) perde dela, e o
                       véu não aparecia: sobrava só o texto azul. Este seletor é 0,3,2 e ganha.

                       O texto vive num textarea, que traz a cor do .form-control e não herda a do
                       td — por isso a regra de cor desce até ele, e até o :disabled, que é como a
                       planilha fica para quem importa. */
                    /* Cada célula pinta o próprio fundo, então a faixa é montada peça a peça e as peças
                       têm de casar na emenda. O corpo é um gradiente VERTICAL: a sombra em cima e embaixo,
                       branco entre elas, e igual em toda a largura — por ser uniforme na horizontal,
                       emenda sem costura com o vizinho. */
                    .malha-table.table > :not(caption) > * > td.malha-sel {
                        background-image:
                            linear-gradient(to bottom,
                                ${veuParadas(true)},
                                ${veuParadas(false)});
                    }
                    /* As pontas fecham a faixa com a borda de FORA, no mesmo raio e na mesma curva. O azul
                       entra pela borda externa e morre nos mesmos pixels, então o lado que encosta no
                       vizinho fica limpo e a emenda continua invisível. Elas repetem o corpo porque a
                       propriedade substitui a lista de camadas inteira. */
                    .malha-table.table > :not(caption) > * > td.malha-sel-ini {
                        background-image:
                            linear-gradient(to right, ${veuParadas(true)}),
                            linear-gradient(to bottom,
                                ${veuParadas(true)},
                                ${veuParadas(false)});
                    }
                    .malha-table.table > :not(caption) > * > td.malha-sel-fim {
                        background-image:
                            linear-gradient(to left, ${veuParadas(true)}),
                            linear-gradient(to bottom,
                                ${veuParadas(true)},
                                ${veuParadas(false)});
                    }
                    /* O texto do lado marcado, só na cor — sem peso. O véu e o azul já dizem que
                       a faixa está escolhida, e mexer no peso da fonte movia a largura do texto em
                       toda a linha. Ele vive num textarea, que traz a cor do .form-control e NÃO herda a do td, por
                       isso a regra desce até ele e até o :disabled, que é como a planilha fica
                       para quem importa. Mesmo seletor longo do fundo, pela mesma razão: precisa
                       ganhar da regra de célula do Bootstrap e do .form-control. */
                    .malha-table.table > :not(caption) > * > td.malha-sel,
                    .malha-table.table > :not(caption) > * > td.malha-sel textarea,
                    .malha-table.table > :not(caption) > * > td.malha-sel textarea:disabled {
                        color: ${VEU_TEXTO};
                    }
                    .malha-lado-click { cursor: pointer; }
                    /* O alcance do copiar e do colar (\`marcarCopia\`). Sombra interna, e não fundo: vai
                       por cima do destaque e da cor de conferência sem apagá-los, e o mesmo seletor
                       longo ganha da sombra que o Bootstrap usa no accent da tabela. O tracejado do
                       copiado é contorno, que não briga com o véu da linha marcada. */
                    .malha-table.table > :not(caption) > * > td.malha-alvo { box-shadow: inset 0 0 0 9999px rgba(45, 127, 249, 0.07); }
                    .malha-table.table > :not(caption) > * > td.malha-previa-copiar { box-shadow: inset 0 0 0 9999px rgba(45, 127, 249, 0.2); }
                    .malha-table.table > :not(caption) > * > td.malha-previa-colar { box-shadow: inset 0 0 0 9999px rgba(245, 158, 11, 0.28); }
                    .malha-table.table > :not(caption) > * > td.malha-copiado { outline: 1.5px dashed ${BRAND_BLUE}; outline-offset: -3px; }
                    /* O foco do painel de trechos. Sombra interna, como a do copiar: pisca por cima do
                       destaque e da cor de conferência sem apagá-los, e some sozinha no fim. */
                    .malha-table.table > :not(caption) > tr.malha-foco > td { animation: malha-foco ${DURACAO_FOCO}ms ease-out; }
                    @keyframes malha-foco {
                        0%, 45% { box-shadow: inset 0 0 0 9999px rgba(45, 127, 249, 0.24); }
                        100% { box-shadow: inset 0 0 0 9999px rgba(45, 127, 249, 0); }
                    }
                    /* O conteúdo de TODA célula de dado é um textarea, e na planilha só de
                       leitura ele está desabilitado. Quem recebe o clique num controle
                       desabilitado varia entre navegadores — uns entregam ao elemento, outros
                       ao pai —, e com o clique parando no textarea a linha do lado não marcava:
                       só o checkbox funcionava. Sem eventos de ponteiro o alvo é sempre a
                       célula. Não se perde nada: textarea desabilitado não aceita foco,
                       digitação nem seleção. Habilitado (o Aéreo montando a malha) ele
                       continua intacto, e aí o clique é dele mesmo. */
                    .malha-table textarea:disabled { pointer-events: none; }
                `}</style>
                <table ref={tabelaRef} className="malha-table table table-bordered table-sm mb-0" style={{ tableLayout: 'fixed', width: 'max-content', zoom }}>
                    <colgroup>
                        {visibleCols.map(col => <col key={col.key} style={{ width: getW(col.key) }} />)}
                    </colgroup>
                    <thead>
                        {/* Grupo headers — `sticky` em cada th, e não no thead, que
                            nem todo navegador prende. zIndex acima das células, que
                            têm campo posicionado dentro. */}
                        <tr ref={linhaGruposRef}>
                            {GROUPS.filter(g => groupCols(g.key) > 0).map(g => {
                                // O grupo sem título fica sobre a rota: para quem exclui, é ali o marcar todas.
                                const todos = g.key === null && excluivel;
                                return (
                                    <th key={g.key ?? '__base'} colSpan={groupCols(g.key)}
                                        className={'fw-bold text-uppercase text-nowrap ' + (todos ? 'text-start' : 'text-center')}
                                        style={{ ...thGroup, position: 'sticky', top: 0, zIndex: 3 }}>
                                        {todos ? (
                                            <button type="button" onClick={alternarTodasParaExcluir} disabled={!idsMarcaveis.length}
                                                aria-pressed={todasParaExcluir}
                                                title={todasParaExcluir ? 'Desmarca todas as linhas' : 'Marca todas as linhas'}
                                                className="btn btn-sm d-inline-flex align-items-center"
                                                style={{
                                                    textTransform: 'none', letterSpacing: 0, fontSize: 11, fontWeight: 600, lineHeight: 1.2,
                                                    borderRadius: 8, padding: '2px 8px', whiteSpace: 'nowrap',
                                                    // O branco azulado da tela (o do sub-cabeçalho e dos chips). Aceso,
                                                    // o véu das linhas marcadas: a sombra azul por dentro, no mesmo raio
                                                    // (`VEU_PX`) e no mesmo tom, com a borda no azul da marca.
                                                    background: BRAND_SKY,
                                                    color: VEU_TEXTO,
                                                    border: `1px solid ${todasParaExcluir ? BRAND_BLUE : BRAND_BORDER}`,
                                                    boxShadow: todasParaExcluir ? `inset 0 0 ${VEU_PX}px ${veuAzul(2)}` : 'none',
                                                }}>
                                                Todos ABC ⇄ XYZ
                                            </button>
                                        ) : g.label}
                                    </th>
                                );
                            })}
                        </tr>
                        {/* Sub-headers com resize handle. `sticky` também posiciona,
                            então o handle absoluto segue ancorado no th. */}
                        <tr>
                            {visibleCols.map(col => (
                                <th key={col.key}
                                    className="fw-bold text-uppercase text-center text-nowrap"
                                    style={{ ...thSub, position: 'sticky', top: alturaGrupos, zIndex: 3, userSelect: 'none', overflow: 'hidden' }}>
                                    {col.toggle ? (
                                        <button type="button" onClick={() => setDetalhes(d => !d)}
                                            title={detalhes
                                                ? 'Ver no padrão de exportação do cliente'
                                                : 'Ver as informações completas da cotação'}
                                            className="btn btn-sm d-inline-flex align-items-center justify-content-center"
                                            style={{
                                                background: detalhes ? BRAND_NAVY : '#fff',
                                                border: `1px solid ${BRAND_BORDER}`,
                                                color: detalhes ? '#fff' : BRAND_NAVY,
                                                borderRadius: 8, padding: '2px 6px', lineHeight: 1,
                                            }}>
                                            <FaEye size={12} />
                                        </button>
                                    ) : col.selecao ? (
                                        <CabecalhoSelecao lado={col.lado} resumo={resumoSelecao?.[col.lado]}
                                            onAlternarTodos={selecao.onAlternarTodos} />
                                    ) : col.label}
                                    {!col.selecao && (
                                        <div
                                            onMouseDown={e => startResize(e, col.key)}
                                            style={{
                                                position: 'absolute', right: 0, top: 0, bottom: 0,
                                                width: 5, cursor: 'col-resize',
                                                background: 'transparent',
                                            }}
                                        />
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {gruposTabela.map(grupo => {
                            // TKT calculado é valor ÚNICO do grupo (= fórmulas do export).
                            const grupoOps = grupo.rows.map(({ op }) => op);
                            const pax = paxDoBloco(grupo);
                            const derivado = {
                                tktPessoa: tktPorPessoaStr(grupoOps),
                                tktGrupo:  tktPorGrupoStr(grupoOps, pax),
                            };
                            // Marcadas para excluir, o grupo se parte onde a linha muda de estado, sem mudar
                            // a ordem: cada trecho de linhas vizinhas, marcadas ou não, é um bloco, como se
                            // fosse outro grupo, e a célula mesclada não atravessa de um para o outro. Grupo
                            // todo marcado, ou nada marcado, é um bloco só, como antes.
                            const blocos = [];
                            grupo.rows.forEach(r => {
                                const marcada = paraExcluir.has(String(r.op._id));
                                const ultimo = blocos[blocos.length - 1];
                                if (ultimo && ultimo.marcada === marcada) ultimo.linhas.push(r);
                                else blocos.push({ marcada, linhas: [r] });
                            });
                            return (
                                <React.Fragment key={grupo.key}>
                                    {blocos.flatMap(({ linhas }) => {
                                        // Merge por valor: cada coluna mesclável vira runs de rowspan no bloco.
                                        // tktPessoa/tktGrupo usam o valor do grupo → mesclam o bloco inteiro.
                                        const mergeInfo = {};
                                        MERGE_KEYS.forEach(key => {
                                            const valores = (key in derivado)
                                                ? linhas.map(() => derivado[key])
                                                : linhas.map(({ op }) => valorMerge(op, key));
                                            mergeInfo[key] = mesclarColuna(valores, extrasDaMescla(grupo.key, key, trechos));
                                        });
                                        return linhas.map(({ op, i }, gi) => (
                                            <tr key={op._id ?? i} className={focadas.has(String(op._id)) ? 'malha-foco' : undefined}>
                                                {visibleCols.map(col => {
                                                    const merge = MERGE_KEYS.includes(col.key) ? mergeInfo[col.key][gi] : undefined;
                                                    // A mesclada cobre as linhas do rowspan dela; as outras, só a própria.
                                                    const cobertas = merge ? linhas.slice(gi, gi + merge.span).map(r => r.op) : [op];
                                                    const celula = marcarCopia(col, vestirLinha(col, vestirLado(col, op, renderCelula(col, op, i, merge, linhas)), cobertas), cobertas);
                                                    return celula && comMenu
                                                        ? React.cloneElement(celula, { onContextMenu: (e) => abrirMenu(e, col, op, i) })
                                                        : celula;
                                                })}
                                            </tr>
                                        ));
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                    {/* `sticky` em cada td, como no cabeçalho: o rodapé fica à vista com a tabela
                        rolada. zIndex acima das células, que têm campo posicionado dentro. */}
                    <tfoot>
                        <tr title="TOTAL ESTIMADO: soma do TKT por grupo, a mesma fórmula do Modelo Cliente.">
                            {linhaRodape('Total estimado', totais.estimado, totais.participantes,
                                { position: 'sticky', bottom: alturaTktMedio, zIndex: 3 })}
                        </tr>
                        <tr ref={linhaTktMedioRef} title="TKT MÉDIO: total estimado dividido pelos participantes, a mesma fórmula do Modelo Cliente.">
                            {linhaRodape('TKT médio', totais.medio, null,
                                { position: 'sticky', bottom: 0, zIndex: 3 })}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
        {/* Espaço do rodapé fixo, medido: substitui o espaçador chutado do ErpTemplate. */}
        {espacoFinal > 0 && <div aria-hidden="true" style={{ height: espacoFinal }} />}
        {menu && (
            <MenuContextoMalha
                posicao={{ x: menu.x, y: menu.y }}
                alvo={menu.alvosLinha.length > 1 ? `${menu.alvosLinha.length} linhas marcadas` : 'Esta linha'}
                rotuloDestaque={menu.campo === LINHA ? 'Destacar linha' : 'Destacar'}
                destaque={(opcoes[menu.alvosDestaque[0]]?.destaques || {})[menu.campo] || null}
                coresUsadas={coresUsadas}
                onDestacar={destacar}
                onCopiar={copiar}
                motivoNaoCopiar={motivoNaoCopiar}
                onColar={colar}
                onDuplicar={onDuplicar ? duplicar : null}
                copiado={aColar ? rotuloCopia(aColar) + (aColar.ids ? '' : ', do clipboard') : null}
                clipboardInvalido={clipboardInvalido}
                motivoNaoColar={motivoNaoColar}
                motivoNaoDuplicar={motivoNaoDuplicar}
                onPrever={setPrevia}
                onFechar={fecharMenu} />
        )}
        <ConfirmarAcaoModal
            aberto={!!colagemPendente}
            titulo="Colar em opção que está no evento?"
            subtitulo={colagemPendente && (colagemPendente.noEvento === colagemPendente.alvosLinha.length
                ? (colagemPendente.noEvento === 1
                    ? 'A linha onde vai colar está nas opções do evento.'
                    : `As ${colagemPendente.noEvento} linhas onde vai colar estão nas opções do evento.`)
                : `${colagemPendente.noEvento} das ${colagemPendente.alvosLinha.length} linhas onde vai colar estão nas opções do evento.`)}
            aviso="A colagem muda a linha da malha, e não a opção do evento: a Importação das Opções de Aéreo mostra a diferença ao RSVP."
            rotulo="Colar mesmo assim"
            icone="aviso"
            onConfirmar={() => {
                aplicarColagem(colagemPendente);
                setColagemPendente(null);
            }}
            onFechar={() => setColagemPendente(null)} />
        </>
    );
}
